import { Alpine } from '../core/alpine-core.js'
import { apiFetch } from './api.js';
import { CURRENT_SEASON } from '../core/current-season.js';

const ymdToday = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const SCORELINE_OPTIONS = [
  { value: '2-0', home: 2, away: 0, label: 'Home 2-0' },
  { value: '2-1', home: 2, away: 1, label: 'Home 2-1' },
  { value: '1-2', home: 1, away: 2, label: 'Away 1-2' },
  { value: '0-2', home: 0, away: 2, label: 'Away 0-2' },
];

const buildSinglesMatch = () => ({
  home: { playerId: '', legs: 0, closes: [], tons: '', borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' },
  away: { playerId: '', legs: 0, closes: [], tons: '', borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' }
});

const buildDoublesMatch = () => ({
  home: {
    p1: { playerId: '', tons: '', closes: [], borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' },
    p2: { playerId: '', tons: '', closes: [], borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' },
    legs: 0
  },
  away: {
    p1: { playerId: '', tons: '', closes: [], borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' },
    p2: { playerId: '', tons: '', closes: [], borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' },
    legs: 0
  }
});

const parseRoute = () => {
  const parts = location.pathname.split('/').filter(Boolean);
  // Use the last "scoresheet" in case there are nested paths
  const i = parts.lastIndexOf('scoresheet');
  if (i === -1) return { mode: 'unknown' };

  const prefix = '/' + parts.slice(0, i).join('/');   // '' or '/admin' etc.
  const seg1 = parts[i + 1] || null;                  // 'add' | 'new' | ':id' | null
  const seg2 = parts[i + 2] || null;                  // 'edit' | null

  // New/Add/Create modes (support multiple aliases)
  if (seg1 === 'add' || seg1 === 'new' || seg1 === 'create') {
    return { mode: 'add', prefix };
  }

  // Edit mode: /scoresheet/:id/edit
  if (seg1 && seg2 === 'edit') {
    return { mode: 'edit', sheetId: seg1, prefix };
  }

  // Optional: treat /scoresheet/:id as edit when no trailing 'edit'
  if (seg1 && !seg2) {
    return { mode: 'edit', sheetId: seg1, prefix };
  }

  return { mode: 'unknown' };
};

// const readFixtureFromQuery = () => new URL(location.href).searchParams.get('fx');
const removeFxFromUrl = (newId, prefix = '', tab = '') => {
  const base = prefix || '';
  const url = new URL(`${location.origin}${base}/scoresheet/${newId}/edit`);
  if (tab) url.searchParams.set('tab', tab);
  history.replaceState(null, '', `${url.pathname}${url.search}`);
};

const goToScoresheetView = (sheetId) => {
  if (!sheetId) return;
  const path = `/admin/scoresheet/${sheetId}`;
  const router = window.PineconeRouter;
  if (router?.navigate) {
    router.navigate(path);
    return;
  }
  location.assign(path);
};

Alpine.data('scoresheetForm', () => ({

  sheetId: '',           // ← track server id after create
  routePrefix: '',
  isSubmitted: false,    // convenience
  isApproved: false,
  adminOverride: false,

  // ---- Validation rules ----
  requiredSinglesCount: 4,
  requiredDoublesCount: 3,

  /* -------- UI / Upload -------- */
  useTextract: false,
  scanning: { active: false, step: 'idle', pct: 0, msg: '' }, // step: idle|uploading|queued|analyzing|parsing|completed|failed
  ocrJobId: '',
  file: null,
  uploading: false,
  upload: { url: '', key: '' },
  showCompactFixtureBar: false,
  /** Bumped on scroll/resize so sticky offsets recompute when the fixed header height changes */
  scoresheetLayoutBump: 0,

  // --- UI state flags ---
  saving: false,
  submitting: false,
  approving: false,
  autoSaving: false,
  autoSaveDelayMs: 15000,
  autoSaveTimer: null,
  autoSaveEnabled: true,
  lastSavedDraftSignature: '',
  // --- Derived state helpers ---
  get canApprove() {        // only after "submitted", and must have an id
    return !!this.sheetId && this.isSubmitted && !this.isApproved;
  },
  get canSubmitForReview() {
    if (!this.sheetId) return false;
    if (this.isApproved && !this.adminOverride) return false;
    try {
      return this.validateForSubmit().ok;
    } catch {
      return false;
    }
  },
  /** When true, leg lines and checkouts are driven by forfeit (read-only except toggling forfeit off). */
  get forfeitLocked() {
    return this.forfeit && (this.forfeitingTeam === 'home' || this.forfeitingTeam === 'away');
  },
  get isBusy() {
    return this.saving || this.submitting || this.approving || this.uploading;
  },
  get isLocked() {
    // lock the whole form when already approved, unless admin toggles override
    return this.isApproved && !this.adminOverride;
  },
  get canCurrentUserEdit() {
    const session = this.$store?.session;
    if (!session?.me) return false;
    if (session.hasRole?.('admin')) return true;
    const teamId = String(session.me?.registeredTeamId || '');
    return !!teamId && (teamId === String(this.meta.homeTeamId || '') || teamId === String(this.meta.awayTeamId || ''));
  },
  updateCompactFixtureBar() {
    if (window.innerWidth >= 640) {
      this.showCompactFixtureBar = false;
      return;
    }
    this.showCompactFixtureBar = !!this.fixture;
  },
  mobileHeaderHeight() {
    // Track compact + layout bumps so sticky bars and scroll offsets stay in sync
    void this.scoresheetLayoutBump;
    void this.$store?.ui?.headerCompact;
    if (typeof window === 'undefined') return 72;
    if (window.innerWidth < 768) {
      // Match admin header inner bar: h-14 (56px) at top, h-10 (40px) when scrolled (see partials/head.html).
      // Using store + fixed px avoids wrong `offsetHeight` mid-transition (duration-300).
      return this.$store?.ui?.headerCompact ? 40 : 56;
    }
    const el = document.querySelector('header');
    const h = el?.offsetHeight;
    if (Number.isFinite(h) && h > 0) return h;
    return 72;
  },
  mobileCompactBarHeight() {
    if (typeof window !== 'undefined' && window.innerWidth >= 640) return 0;
    return this.$refs.compactFixtureBar?.offsetHeight || (this.fixture && window.innerWidth < 640 ? 54 : 0);
  },
  /** Sticky stack (mobile team strip + section tabs): single top offset below global header. */
  scoresheetStickyHeaderStackStyle() {
    void this.scoresheetLayoutBump;
    void this.$store?.ui?.headerCompact;
    if (typeof window === 'undefined') return '';
    if (window.innerWidth >= 768) return 'top: 2.6rem';
    return `top: ${this.mobileHeaderHeight()}px`;
  },
  mobileContentTopPadding() {
    if (window.innerWidth >= 640 || !this.fixture) return '0px';
    return `${this.mobileCompactBarHeight() + 8}px`;
  },

  /* -------- Meta (fixture-driven) -------- */
  fixtureHeader: null,
  fixture: null,
  lockFixture: false, // when true, team & date are read-only (from fixture)
  meta: {
    fixtureId: '',          // set from ?fx= in init()
    homeTeamId: '',
    awayTeamId: '',
    date: ymdToday(),
    round: '',
    league: '',
    season: ''
  },

  /* -------- Data sources -------- */
  teams: [],
  allPlayers: [],
  roster: { home: [], away: [] },
  borrowPicker: {
    open: false,
    search: '',
    side: 'home',
    target: null,
  },

  /* -------- Scores -------- */
  scorelineOptions: SCORELINE_OPTIONS,
  singles: Array.from({ length: 4 }).map(buildSinglesMatch),
  doubles: Array.from({ length: 3 }).map(buildDoublesMatch),
  beer: {
    homePlayerIds: [],
    awayPlayerIds: [],
    homeTons: {}, homeCloses: {},
    awayTons: {}, awayCloses: {},
    winner: ''          // 'home' | 'away'
  },

  /** Match forfeited: no checkouts; forfeiting side gets no player stats on approval. */
  forfeit: false,
  /** '' | 'home' | 'away' — side that did not show / forfeited */
  forfeitingTeam: '',
  forfeitReason: '',
  /** UI: forfeit <details> open state (do not derive from forfeit alone — avoids fighting the native toggle). */
  forfeitPanelOpen: false,

  totals: { singles: { home: 0, away: 0 }, doubles: { home: 0, away: 0 } },

  loading: true,

  previewOpen: false,

  // ---- Validation rules ----
  requiredSinglesCount: 4,
  requiredDoublesCount: 3,

  // UI
  sectionTab: 'singles',   // 'singles' | 'doubles' | 'beer' | 'photo'
  singlesIndex: 0,
  doublesIndex: 0,
  /** When true (md+), show all match cards; when false, one-at-a-time wizard like phone. */
  scoresheetWideLayout: false,

  bindScoresheetWideLayout() {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => { this.scoresheetWideLayout = mq.matches; };
    apply();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(apply);
    }
  },
  isSinglesCardVisible(i) {
    return this.scoresheetWideLayout || i === this.singlesIndex;
  },
  isDoublesCardVisible(i) {
    return this.scoresheetWideLayout || i === this.doublesIndex;
  },

  normalizedSectionTab(value) {
    return ['singles', 'doubles', 'beer', 'photo'].includes(value) ? value : 'singles';
  },
  syncSectionTabToUrl() {
    const url = new URL(location.href);
    url.searchParams.set('tab', this.sectionTab);
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
  },
  restoreSectionTabFromUrl() {
    const tab = new URL(location.href).searchParams.get('tab');
    this.sectionTab = this.normalizedSectionTab(tab || 'singles');
  },

  switchSection(section) {
    this.sectionTab = this.normalizedSectionTab(section);
    this.syncSectionTabToUrl();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    this.$nextTick(() => {
      this.resetActivePanelScroll(this.sectionTab);
    });
  },

  getScrollOffset(section, target = null) {
    void this.scoresheetLayoutBump;
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return 120;

    const gap = section === 'beer' ? 18 : 14;
    const headerHeight = this.mobileHeaderHeight();
    const stickyH = this.$refs.scoresheetStickyHeader?.offsetHeight || 0;
    return headerHeight + stickyH + gap;
  },

  scrollToSection(section) {
    const el = this.$refs[section + 'Heading'] || this.$refs[section + 'Section'];
    if (!el) return;

    const offset = this.getScrollOffset(section, el);

    const targetTop = el.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    });
  },
  scrollToActiveMatch(section, index = 0) {
    this.$nextTick(() => {
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        this.scrollToSection(section);
        return;
      }
      const root = this.$refs[section + 'Section'];
      if (!root) {
        this.scrollToSection(section);
        return;
      }
      const cards = root.querySelectorAll('[data-match-card]');
      const activeCard = cards?.[index];
      const target = activeCard || this.$refs[section + 'Heading'] || root;
      const offset = this.getScrollOffset(section, target);
      const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth'
      });
    });
  },
  resetActivePanelScroll(section, index = null) {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    this.$nextTick(() => {
      if (section === 'beer') {
        if (this.$refs.beerPanel) this.$refs.beerPanel.scrollTop = 0;
        return;
      }

      const root = this.$refs[section + 'Section'];
      if (!root) return;
      const cards = root.querySelectorAll('[data-match-card]');
      const activeIndex = index ?? (section === 'singles' ? this.singlesIndex : this.doublesIndex);
      const activeCard = cards?.[activeIndex];
      if (activeCard) activeCard.scrollTop = 0;
    });
  },

  scrollScoresheetPageTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
  goSingles(delta) {
    this.scrollScoresheetPageTop();
    const max = this.singles.length - 1;
    this.singlesIndex = Math.min(max, Math.max(0, this.singlesIndex + delta));
    this.resetActivePanelScroll('singles', this.singlesIndex);
  },
  goDoubles(delta) {
    this.scrollScoresheetPageTop();
    const max = this.doubles.length - 1;
    this.doublesIndex = Math.min(max, Math.max(0, this.doublesIndex + delta));
    this.resetActivePanelScroll('doubles', this.doublesIndex);
  },

  openPreview() {
    if (this.upload && this.upload.url) {
      this.previewOpen = true;
    }
  },

  clearUpload() {
    // Clear the upload object however you currently manage it
    this.upload = { url: '', key: '', ...this.upload, url: '' };

    // Reset the file input element if present
    if (this.$refs.fileInput) {
      this.$refs.fileInput.value = '';
    }
  },

  _validId(v) { return typeof v === 'string' && v.trim().length > 0; },
  _num(n) { const x = Number(n); return Number.isFinite(x) ? x : NaN; },
  parseCSV(s) {
    return String(s || '').split(',').map(x => x.trim()).filter(Boolean);
  },
  parseCloseArray(value, count = null) {
    const parsed = Array.isArray(value)
      ? value.map(x => String(x || '').trim()).filter(Boolean)
      : this.parseCSV(value);
    if (count == null) return parsed;
    return Array.from({ length: count }, (_, i) => parsed[i] || '');
  },
  clampLegs(n) {
    const value = Number(n) || 0;
    return Math.max(0, Math.min(2, value));
  },
  scorelineFor(match) {
    return `${Number(match?.home?.legs) || 0}-${Number(match?.away?.legs) || 0}`;
  },
  singlesResultOptionLabel(match, opt) {
    if (!opt) return '';
    const winnerSide = Number(opt.home) > Number(opt.away) ? 'home' : 'away';
    const winnerTeam = this.teamName(winnerSide);
    const winnerPlayerId = match?.[winnerSide]?.playerId || '';
    const winnerPlayer = winnerPlayerId ? this.playerName(winnerPlayerId, winnerSide) : 'TBC';
    return `${winnerTeam} (${winnerPlayer}) ${opt.home}-${opt.away}`;
  },
  singlesResultOptionLabelCompact(match, opt) {
    if (!opt) return '';
    const winnerSide = Number(opt.home) > Number(opt.away) ? 'home' : 'away';
    return `${this.teamName(winnerSide)} ${opt.home}-${opt.away}`;
  },
  singlesResultOptionLabelResponsive(match, opt) {
    return this.scoresheetWideLayout
      ? this.singlesResultOptionLabel(match, opt)
      : this.singlesResultOptionLabelCompact(match, opt);
  },
  singlesResultSummaryLabel(match) {
    const selected = this.scorelineOptions.find(opt => opt.value === this.scorelineFor(match));
    if (!selected) return 'Set result';
    return this.singlesResultOptionLabelResponsive(match, selected);
  },
  doublesWinnerPlayerNames(match, side) {
    const p1 = match?.[side]?.p1?.playerId ? this.playerName(match[side].p1.playerId, side) : 'TBC';
    const p2 = match?.[side]?.p2?.playerId ? this.playerName(match[side].p2.playerId, side) : 'TBC';
    return `${p1} & ${p2}`;
  },
  doublesResultOptionLabel(match, opt) {
    if (!opt) return '';
    const winnerSide = Number(opt.home) > Number(opt.away) ? 'home' : 'away';
    const winnerTeam = this.teamName(winnerSide);
    const winners = this.doublesWinnerPlayerNames(match, winnerSide);
    return `${winnerTeam} (${winners}) ${opt.home}-${opt.away}`;
  },
  doublesResultOptionLabelCompact(match, opt) {
    if (!opt) return '';
    const winnerSide = Number(opt.home) > Number(opt.away) ? 'home' : 'away';
    return `${this.teamName(winnerSide)} ${opt.home}-${opt.away}`;
  },
  doublesResultOptionLabelResponsive(match, opt) {
    return this.scoresheetWideLayout
      ? this.doublesResultOptionLabel(match, opt)
      : this.doublesResultOptionLabelCompact(match, opt);
  },
  doublesResultSummaryLabel(match) {
    const selected = this.scorelineOptions.find(opt => opt.value === this.scorelineFor(match));
    if (!selected) return 'Set result';
    return this.doublesResultOptionLabelResponsive(match, selected);
  },
  isValidScoreline(home, away) {
    return this.scorelineOptions.some(opt => opt.home === home && opt.away === away);
  },
  applyScoreline(match, value, mode = 'singles') {
    if (this.forfeitLocked) return;
    const selected = this.scorelineOptions.find(opt => opt.value === value) || this.scorelineOptions[0];
    match.home.legs = selected.home;
    match.away.legs = selected.away;
    if (mode === 'singles') {
      this.syncSinglesClosings(match);
    } else {
      this.syncDoublesClosings(match, 'home');
      this.syncDoublesClosings(match, 'away');
    }
    this.recalc();
  },
  applyForfeitScorelines() {
    if (!this.forfeit || (this.forfeitingTeam !== 'home' && this.forfeitingTeam !== 'away')) return;
    const win = this.forfeitingTeam === 'home' ? 'away' : 'home';
    const wh = win === 'home' ? 2 : 0;
    const wa = win === 'away' ? 2 : 0;
    for (let i = 0; i < this.requiredSinglesCount; i++) {
      const m = this.singles[i];
      if (!m) continue;
      m.home.legs = wh;
      m.away.legs = wa;
      m.home.closes = [];
      m.away.closes = [];
      m.home.tons = '';
      m.away.tons = '';
    }
    for (let i = 0; i < this.requiredDoublesCount; i++) {
      const m = this.doubles[i];
      if (!m) continue;
      m.home.legs = wh;
      m.away.legs = wa;
      ['p1', 'p2'].forEach((slot) => {
        m.home[slot].closes = [];
        m.home[slot].tons = '';
        m.away[slot].closes = [];
        m.away[slot].tons = '';
      });
    }
    this.beer.winner = win;
    const clearVals = (o) => {
      if (!o || typeof o !== 'object') return;
      Object.keys(o).forEach((k) => { o[k] = ''; });
    };
    clearVals(this.beer.homeCloses);
    clearVals(this.beer.awayCloses);
    clearVals(this.beer.homeTons);
    clearVals(this.beer.awayTons);
    this.recalc();
  },
  /** True when every singles/doubles slot has a player — required before enabling forfeit (appearances for the non-forfeiting side). */
  areAllSinglesDoublesPlayersAssigned() {
    const n = this.requiredSinglesCount;
    if (this.singles.length < n) return false;
    for (let i = 0; i < n; i++) {
      const m = this.singles[i];
      if (!this._validId(m?.home?.playerId) || !this._validId(m?.away?.playerId)) return false;
    }
    const nd = this.requiredDoublesCount;
    if (this.doubles.length < nd) return false;
    for (let i = 0; i < nd; i++) {
      const m = this.doubles[i];
      if (!this._validId(m?.home?.p1?.playerId) || !this._validId(m?.home?.p2?.playerId)) return false;
      if (!this._validId(m?.away?.p1?.playerId) || !this._validId(m?.away?.p2?.playerId)) return false;
      if (m.home.p1.playerId === m.home.p2.playerId) return false;
      if (m.away.p1.playerId === m.away.p2.playerId) return false;
    }
    return true;
  },
  onForfeitToggle() {
    if (!this.forfeit) {
      this.forfeitingTeam = '';
      this.forfeitReason = '';
      this.forfeitPanelOpen = false;
      return;
    }
    if (!this.areAllSinglesDoublesPlayersAssigned()) {
      this.$nextTick(() => { this.forfeit = false; });
      this.$store?.toastr?.error?.(
        'Assign every player in all Singles and Doubles matches first. Appearances for the team that played still need a full lineup.',
        5000
      );
      return;
    }
    this.forfeitPanelOpen = true;
    this.upload = { url: '', key: '', ...this.upload, url: '', key: '' };
    if (this.forfeitingTeam !== 'home' && this.forfeitingTeam !== 'away') {
      this.forfeitingTeam = '';
    }
    this.applyForfeitScorelines();
  },
  onForfeitingTeamChange() {
    if (!this.forfeit) return;
    this.applyForfeitScorelines();
  },
  scorelineButtonClass(match, value) {
    const active = this.scorelineFor(match) === value;
    const opt = this.scorelineOptions.find(o => o.value === value);
    const homeWins = opt && opt.home > opt.away;
    const awayWins = opt && opt.away > opt.home;

    if (active) {
      if (homeWins) {
        return 'ring-2 ring-sky-500 border-sky-600 bg-sky-600 text-white shadow-md dark:ring-sky-400 dark:border-sky-500 dark:bg-sky-600';
      }
      if (awayWins) {
        return 'ring-2 ring-rose-500 border-rose-600 bg-rose-600 text-white shadow-md dark:ring-rose-400 dark:border-rose-500 dark:bg-rose-600';
      }
    }
    if (homeWins) {
      return 'border-sky-300 bg-sky-50 text-sky-950 hover:border-sky-400 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100 dark:hover:bg-sky-950/55';
    }
    if (awayWins) {
      return 'border-rose-300 bg-rose-50 text-rose-950 hover:border-rose-400 hover:bg-rose-100 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-100 dark:hover:bg-rose-950/55';
    }
    return 'border-neutral-300 bg-white text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200';
  },
  /** True when this side has won the match (singles or doubles scoreline). */
  matchSideIsWinner(match, side) {
    const h = this.clampLegs(match?.home?.legs);
    const a = this.clampLegs(match?.away?.legs);
    if (h === 0 && a === 0) return false;
    if (h > a) return side === 'home';
    if (a > h) return side === 'away';
    return false;
  },
  singlesClosesPanelTitle(match, side) {
    const legs = this.clampLegs(side === 'home' ? match?.home?.legs : match?.away?.legs);
    const pid = side === 'home' ? match?.home?.playerId : match?.away?.playerId;
    const closeWord = legs === 1 ? 'close' : 'closes';
    if (pid) return `${this.playerName(pid, side)} ${closeWord}`;
    return side === 'home' ? `Home ${closeWord}` : `Away ${closeWord}`;
  },
  sidePanelClass(side, match = null) {
    const baseHome = 'border-sky-200 bg-sky-50/70 dark:border-sky-900/40 dark:bg-sky-950/20';
    const baseAway = 'border-rose-200 bg-rose-50/70 dark:border-rose-900/40 dark:bg-rose-950/20';
    const base = side === 'home' ? baseHome : baseAway;
    if (match && this.matchSideIsWinner(match, side)) {
      return `${base} ring-2 ring-amber-400/85 border-amber-400/70 shadow-[0_10px_28px_rgba(251,191,36,0.2)] dark:ring-amber-400/50 dark:border-amber-500/50 dark:shadow-[0_10px_28px_rgba(251,191,36,0.12)]`;
    }
    return base;
  },
  syncSinglesClosings(match) {
    match.home.closes = this.parseCloseArray(match.home.closes, this.clampLegs(match.home.legs));
    match.away.closes = this.parseCloseArray(match.away.closes, this.clampLegs(match.away.legs));
  },
  closeCountOptions(legs, otherCount = 0) {
    const max = Math.max(0, this.clampLegs(legs) - Math.max(0, Number(otherCount) || 0));
    return Array.from({ length: max + 1 }, (_, i) => i);
  },
  setPlayerCloseCount(player, count) {
    player.closes = this.parseCloseArray(player.closes, Math.max(0, Number(count) || 0));
  },
  closeSplitOptions(legs) {
    const max = this.clampLegs(legs);
    return Array.from({ length: max + 1 }, (_, i) => i);
  },
  setDoublesCloseSplit(match, side, p1Count) {
    const target = this.clampLegs(match?.[side]?.legs);
    const merged = [
      ...this.parseCloseArray(match?.[side]?.p1?.closes),
      ...this.parseCloseArray(match?.[side]?.p2?.closes),
    ].slice(0, target);
    const safeP1Count = Math.max(0, Math.min(target, Number(p1Count) || 0));
    match[side].p1.closes = Array.from({ length: safeP1Count }, (_, i) => merged[i] || '');
    match[side].p2.closes = Array.from({ length: target - safeP1Count }, (_, i) => merged[safeP1Count + i] || '');
  },
  doublesSplitButtonClass(active) {
    return active
      ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900'
      : 'border-neutral-300 bg-white text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200';
  },
  syncDoublesClosings(match, side) {
    const target = this.clampLegs(match?.[side]?.legs);
    const p1 = this.parseCloseArray(match?.[side]?.p1?.closes);
    const p2 = this.parseCloseArray(match?.[side]?.p2?.closes);
    const merged = [...p1, ...p2].slice(0, target);
    const fallbackMerged = merged.length === target
      ? merged
      : Array.from({ length: target }, (_, i) => merged[i] || '');
    const hasExistingSplit = (p1.length + p2.length) > 0;
    const p1Count = hasExistingSplit
      ? Math.min(p1.length, fallbackMerged.length)
      : fallbackMerged.length;
    match[side].p1.closes = Array.from({ length: p1Count }, (_, i) => fallbackMerged[i] || '');
    match[side].p2.closes = Array.from({ length: fallbackMerged.length - p1Count }, (_, i) => fallbackMerged[p1Count + i] || '');
  },
  filledCloseCount(value) {
    return this.parseCloseArray(value).length;
  },
  teamCloseCount(match, side) {
    return this.filledCloseCount(match?.[side]?.p1?.closes) + this.filledCloseCount(match?.[side]?.p2?.closes);
  },

  _validateSingles() {
    const errs = [];
    const n = this.requiredSinglesCount;
    if (this.singles.length < n) errs.push(`Singles must have ${n} rows.`);
    for (let i = 0; i < n; i++) {
      const m = this.singles[i];
      if (!this._validId(m.home.playerId)) errs.push(`Singles #${i + 1}: select Home player.`);
      if (!this._validId(m.away.playerId)) errs.push(`Singles #${i + 1}: select Away player.`);
      const hl = this._num(m.home.legs); const al = this._num(m.away.legs);
      if (!this.isValidScoreline(hl, al)) errs.push(`Singles #${i + 1}: choose a valid result.`);
      if (this.filledCloseCount(m.home.closes) !== hl) errs.push(`Singles #${i + 1}: Home closes must match legs won.`);
      if (this.filledCloseCount(m.away.closes) !== al) errs.push(`Singles #${i + 1}: Away closes must match legs won.`);
    }
    return errs;
  },

  _validateDoubles() {
    const errs = [];
    const n = this.requiredDoublesCount;
    if (this.doubles.length < n) errs.push(`Doubles must have ${n} rows.`);
    for (let i = 0; i < n; i++) {
      const m = this.doubles[i];
      // players
      const reqs = [
        ['Home P1', m.home.p1.playerId],
        ['Home P2', m.home.p2.playerId],
        ['Away P1', m.away.p1.playerId],
        ['Away P2', m.away.p2.playerId],
      ];
      reqs.forEach(([label, id]) => {
        if (!this._validId(id)) errs.push(`Doubles #${i + 1}: select ${label}.`);
      });
      // duplicates within side
      if (this._validId(m.home.p1.playerId) && m.home.p1.playerId === m.home.p2.playerId) {
        errs.push(`Doubles #${i + 1}: Home players must be different.`);
      }
      if (this._validId(m.away.p1.playerId) && m.away.p1.playerId === m.away.p2.playerId) {
        errs.push(`Doubles #${i + 1}: Away players must be different.`);
      }
      // legs
      const hl = this._num(m.home.legs); const al = this._num(m.away.legs);
      if (!this.isValidScoreline(hl, al)) errs.push(`Doubles #${i + 1}: choose a valid result.`);
      if (this.teamCloseCount(m, 'home') !== hl) errs.push(`Doubles #${i + 1}: Home closes must match legs won.`);
      if (this.teamCloseCount(m, 'away') !== al) errs.push(`Doubles #${i + 1}: Away closes must match legs won.`);
    }
    return errs;
  },

  _validateMeta() {
    const errs = [];
    if (!this._validId(this.meta.fixtureId)) errs.push('Fixture is required.');
    if (!this._validId(this.meta.homeTeamId)) errs.push('Home team is required.');
    if (!this._validId(this.meta.awayTeamId)) errs.push('Away team is required.');
    if (!this.meta.date) errs.push('Match date is required.');
    return errs;
  },

  _validateBeerForSubmit() {
    const errs = [];
    const winner = this.beer?.winner;
    if (winner !== 'home' && winner !== 'away') {
      errs.push('Beer Leg winner must be selected before submitting.');
      return errs;
    }

    const playerIds = winner === 'home' ? this.beer.homePlayerIds : this.beer.awayPlayerIds;
    if (!Array.isArray(playerIds) || playerIds.length === 0) {
      errs.push('Beer Leg winner players are missing. Complete Singles first.');
      return errs;
    }

    const closesMap = winner === 'home' ? this.beer.homeCloses : this.beer.awayCloses;
    const hasCheckout = playerIds.some(pid => {
      const raw = closesMap?.[pid] ?? '';
      return this.parseCSV(raw).length > 0;
    });

    if (!hasCheckout) {
      errs.push('Enter a checkout for the Beer Leg winner.');
    }

    return errs;
  },

  _validatePhotoForSubmit() {
    if (this.forfeit) return [];
    const url = String(this.upload?.url || '').trim();
    if (!url) {
      return ['Add a scoresheet photo on the Photo tab before submitting for review.'];
    }
    return [];
  },

  _validateForfeitSubmit() {
    const errs = [];
    if (this.forfeitingTeam !== 'home' && this.forfeitingTeam !== 'away') {
      errs.push('Select which team forfeited.');
    }
    const reason = String(this.forfeitReason || '').trim();
    if (!reason) errs.push('Enter a reason for the forfeit.');
    const win = this.forfeitingTeam === 'home' ? 'away' : 'home';
    const wh = win === 'home' ? 2 : 0;
    const wa = win === 'away' ? 2 : 0;
    const n = this.requiredSinglesCount;
    if (this.singles.length < n) errs.push(`Singles must have ${n} rows.`);
    for (let i = 0; i < n; i++) {
      const m = this.singles[i];
      if (!this._validId(m.home.playerId)) errs.push(`Singles #${i + 1}: select Home player.`);
      if (!this._validId(m.away.playerId)) errs.push(`Singles #${i + 1}: select Away player.`);
      const hl = this._num(m.home.legs); const al = this._num(m.away.legs);
      if (hl !== wh || al !== wa) errs.push(`Singles #${i + 1}: forfeit score must be ${wh}-${wa} (use “Match forfeited” to apply).`);
      if (this.filledCloseCount(m.home.closes) > 0 || this.filledCloseCount(m.away.closes) > 0) {
        errs.push(`Singles #${i + 1}: checkouts cannot be recorded on a forfeit.`);
      }
    }
    const nd = this.requiredDoublesCount;
    if (this.doubles.length < nd) errs.push(`Doubles must have ${nd} rows.`);
    for (let i = 0; i < nd; i++) {
      const m = this.doubles[i];
      const reqs = [
        ['Home P1', m.home.p1.playerId],
        ['Home P2', m.home.p2.playerId],
        ['Away P1', m.away.p1.playerId],
        ['Away P2', m.away.p2.playerId],
      ];
      reqs.forEach(([label, id]) => {
        if (!this._validId(id)) errs.push(`Doubles #${i + 1}: select ${label}.`);
      });
      const hl = this._num(m.home.legs); const al = this._num(m.away.legs);
      if (hl !== wh || al !== wa) errs.push(`Doubles #${i + 1}: forfeit score must be ${wh}-${wa}.`);
      if (this.teamCloseCount(m, 'home') > 0 || this.teamCloseCount(m, 'away') > 0) {
        errs.push(`Doubles #${i + 1}: checkouts cannot be recorded on a forfeit.`);
      }
    }
    if (this.beer.winner !== win) errs.push('Beer leg winner must be the non-forfeiting team.');
    const winner = this.beer.winner;
    if (winner !== 'home' && winner !== 'away') {
      errs.push('Beer Leg winner must be selected.');
    } else {
      const playerIds = winner === 'home' ? this.beer.homePlayerIds : this.beer.awayPlayerIds;
      if (!Array.isArray(playerIds) || playerIds.length === 0) {
        errs.push('Beer Leg winner players are missing. Complete Singles first.');
      } else {
        const closesMap = winner === 'home' ? this.beer.homeCloses : this.beer.awayCloses;
        const hasCheckout = playerIds.some(pid => this.parseCSV(closesMap?.[pid] ?? '').length > 0);
        if (hasCheckout) errs.push('Beer leg checkouts cannot be recorded on a forfeit.');
      }
    }
    return errs;
  },

  validateForSubmit() {
    if (this.forfeit) {
      const errors = [
        ...this._validateMeta(),
        ...this._validatePhotoForSubmit(),
        ...this._validateForfeitSubmit(),
      ];
      return { ok: errors.length === 0, errors };
    }
    const errors = [
      ...this._validateMeta(),
      ...this._validatePhotoForSubmit(),
      ...this._validateSingles(),
      ...this._validateDoubles(),
      ...this._validateBeerForSubmit(),
    ];
    return { ok: errors.length === 0, errors };
  },

  _utcIsoToLocalDateAsBangkokWall(isoUtc) {
    const d = new Date(isoUtc)
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(d).reduce((acc, p) => (acc[p.type] = p.value, acc), {})
    const y = +parts.year, m = +parts.month, da = +parts.day, h = +parts.hour, mi = +parts.minute
    return new Date(y, m - 1, da, h, mi)
  },

  hydrateFromSheet(sheet) {
    const toIdStr = (x) => (x && (x._id || x).toString ? (x._id || x).toString() : '');
    const fixtureRef = sheet?.fixtureId && typeof sheet.fixtureId === 'object' ? sheet.fixtureId : null;

    this.sheetId = toIdStr(sheet._id);
    this.isSubmitted = sheet.status === 'submitted';
    this.isApproved = sheet.status === 'approved';

    // ---- Meta / upload ----
    this.meta.fixtureId = toIdStr(sheet.fixtureId) || this.meta.fixtureId;
    this.meta.date = sheet.meta?.date || this.meta.date;
    this.meta.round = sheet.meta?.round || '';
    this.meta.homeTeamId = toIdStr(sheet.meta?.homeTeamId) || '';
    this.meta.awayTeamId = toIdStr(sheet.meta?.awayTeamId) || '';
    this.meta.league = sheet.meta?.league;
    this.meta.season = sheet.meta?.season;
    if (fixtureRef) {
      this.fixture = {
        ...fixtureRef,
        homeTeamName: fixtureRef.homeTeamName || fixtureRef.homeTeamId?.name || 'Home Team',
        awayTeamName: fixtureRef.awayTeamName || fixtureRef.awayTeamId?.name || 'Away Team',
        homeTeamLogo: fixtureRef.homeTeamLogo || fixtureRef.homeTeamId?.logoUrl || '/assets/avatar-placeholder.png',
        awayTeamLogo: fixtureRef.awayTeamLogo || fixtureRef.awayTeamId?.logoUrl || '/assets/avatar-placeholder.png',
      };
    }

    this.upload = {
      url: sheet.meta?.uploadUrl || '',
      key: sheet.meta?.uploadKey || '',
    };

    // Date picker sync
    if (this.$refs?.dateInput?._flatpickr && this.meta.date) {
      this.$refs.dateInput._flatpickr.setDate(this.meta.date, true);
    }

    // ---- Singles (pad to 4 rows) ----
    const singlesSrc = Array.isArray(sheet.singles) ? sheet.singles : [];
    const singlesLen = Math.max(4, singlesSrc.length);
    this.singles = Array.from({ length: singlesLen }).map((_, i) => {
      const s = singlesSrc[i] || {};
      const home = s.home || {}, away = s.away || {};
      return {
        home: {
          playerId: toIdStr(home.playerId) || '',
          legs: Number(home.legs) || 0,
          closes: this.parseCloseArray(home.closes, Number(home.legs) || 0),
          tons: Array.isArray(home.tons) ? home.tons.join(', ') : '',
          borrowed: !!home.borrowed,
          borrowedFromTeamId: toIdStr(home.borrowedFromTeamId) || '',
          borrowedFromTeamName: String(home.borrowedFromTeamName || ''),
        },
        away: {
          playerId: toIdStr(away.playerId) || '',
          legs: Number(away.legs) || 0,
          closes: this.parseCloseArray(away.closes, Number(away.legs) || 0),
          tons: Array.isArray(away.tons) ? away.tons.join(', ') : '',
          borrowed: !!away.borrowed,
          borrowedFromTeamId: toIdStr(away.borrowedFromTeamId) || '',
          borrowedFromTeamName: String(away.borrowedFromTeamName || ''),
        },
      };
    });

    // ---- Doubles (pad to 3 rows) ----
    const doublesSrc = Array.isArray(sheet.doubles) ? sheet.doubles : [];
    const doublesLen = Math.max(3, doublesSrc.length);
    this.doubles = Array.from({ length: doublesLen }).map((_, i) => {
      const d = doublesSrc[i] || {};
      const home = d.home || {}, away = d.away || {};
      const hp = Array.isArray(home.players) ? home.players : [];
      const ap = Array.isArray(away.players) ? away.players : [];
      const hp1 = hp[0] || {}, hp2 = hp[1] || {};
      const ap1 = ap[0] || {}, ap2 = ap[1] || {};
      return {
        home: {
          p1: {
            playerId: toIdStr(hp1.playerId) || '',
            tons: Array.isArray(hp1.tons) ? hp1.tons.join(', ') : '',
            closes: this.parseCloseArray(hp1.closes),
            borrowed: !!hp1.borrowed,
            borrowedFromTeamId: toIdStr(hp1.borrowedFromTeamId) || '',
            borrowedFromTeamName: String(hp1.borrowedFromTeamName || '')
          },
          p2: {
            playerId: toIdStr(hp2.playerId) || '',
            tons: Array.isArray(hp2.tons) ? hp2.tons.join(', ') : '',
            closes: this.parseCloseArray(hp2.closes),
            borrowed: !!hp2.borrowed,
            borrowedFromTeamId: toIdStr(hp2.borrowedFromTeamId) || '',
            borrowedFromTeamName: String(hp2.borrowedFromTeamName || '')
          },
          legs: Number(home.legs) || 0,
        },
        away: {
          p1: {
            playerId: toIdStr(ap1.playerId) || '',
            tons: Array.isArray(ap1.tons) ? ap1.tons.join(', ') : '',
            closes: this.parseCloseArray(ap1.closes),
            borrowed: !!ap1.borrowed,
            borrowedFromTeamId: toIdStr(ap1.borrowedFromTeamId) || '',
            borrowedFromTeamName: String(ap1.borrowedFromTeamName || '')
          },
          p2: {
            playerId: toIdStr(ap2.playerId) || '',
            tons: Array.isArray(ap2.tons) ? ap2.tons.join(', ') : '',
            closes: this.parseCloseArray(ap2.closes),
            borrowed: !!ap2.borrowed,
            borrowedFromTeamId: toIdStr(ap2.borrowedFromTeamId) || '',
            borrowedFromTeamName: String(ap2.borrowedFromTeamName || '')
          },
          legs: Number(away.legs) || 0,
        },
      };
    });

    // ---- Beer leg ----
    const beer = sheet.beer || {};
    const homeBeer = Array.isArray(beer.home) ? beer.home : [];
    const awayBeer = Array.isArray(beer.away) ? beer.away : [];

    this.beer.winner = beer.winner || ''; // '' | 'home' | 'away'

    this.beer.homePlayerIds = homeBeer.map(ps => toIdStr(ps.playerId)).filter(Boolean).slice(0, 4);
    this.beer.awayPlayerIds = awayBeer.map(ps => toIdStr(ps.playerId)).filter(Boolean).slice(0, 4);

    this.beer.homeTons = {};
    this.beer.homeCloses = {};
    for (const ps of homeBeer) {
      const pid = toIdStr(ps.playerId);
      if (!pid) continue;
      this.beer.homeTons[pid] = Array.isArray(ps.tons) ? ps.tons.join(', ') : '';
      this.beer.homeCloses[pid] = Array.isArray(ps.closes) ? ps.closes.join(', ') : '';
    }

    this.beer.awayTons = {};
    this.beer.awayCloses = {};
    for (const ps of awayBeer) {
      const pid = toIdStr(ps.playerId);
      if (!pid) continue;
      this.beer.awayTons[pid] = Array.isArray(ps.tons) ? ps.tons.join(', ') : '';
      this.beer.awayCloses[pid] = Array.isArray(ps.closes) ? ps.closes.join(', ') : '';
    }
    // Once we’ve hydrated from the saved sheet, do not auto-sync from Singles
    // ---- Totals (UI-only) ----
    this.totals = {
      singles: {
        home: Number(sheet.totals?.singles?.home) || 0,
        away: Number(sheet.totals?.singles?.away) || 0,
      },
      doubles: {
        home: Number(sheet.totals?.doubles?.home) || 0,
        away: Number(sheet.totals?.doubles?.away) || 0,
      },
    };

    this.forfeit = !!sheet.forfeit;
    this.forfeitingTeam = sheet.forfeitingTeam === 'home' || sheet.forfeitingTeam === 'away' ? sheet.forfeitingTeam : '';
    this.forfeitReason = String(sheet.forfeitReason || '').trim();
    this.forfeitPanelOpen = !!this.forfeit;

    // ---- Lock fields from fixture once sheet is tied to a fixture ----
    this.lockFixture = !!this.meta.fixtureId;

    // Make sure we purge stale picks after hydration (in case roster didn’t include someone)
    this.clearInvalidPicks('home');
    this.clearInvalidPicks('away');
    this.syncBeerPlayersFromSingles();
    this.singles.forEach(match => this.syncSinglesClosings(match));
    this.doubles.forEach(match => {
      this.syncDoublesClosings(match, 'home');
      this.syncDoublesClosings(match, 'away');
    });
    this.recalc();
    this.markCurrentDraftAsSaved();
  },

  /* -------- Init -------- */
  async init() {
    this.bindScoresheetWideLayout();
    const { mode, sheetId, prefix } = parseRoute();
    this.routePrefix = String(prefix || '');
    const fx = new URL(location.href).searchParams.get('fx');
    this.restoreSectionTabFromUrl();

    if (mode === 'edit') this.sheetId = sheetId || '';
    this.autoSaveEnabled = mode !== 'edit';
    if (mode === 'add') {
      if (!fx) {
        this.$store?.toastr?.error?.('Missing fixture (fx) in URL');
        this.loading = false; return;
      }
      this.meta.fixtureId = fx;
    }

    // 2) Load teams first (so team IDs are valid in selects)
    await Promise.all([this.loadTeams(), this.loadBorrowablePlayers()]);

    try {
      if (mode === 'edit') {
        // ===== /scoresheet/:id/edit =====
        const res = await apiFetch(`/scoresheets/${this.sheetId}?populate=true`);
        const j = await res.json();
        if (!res.ok) throw new Error(j?.message || 'Failed to load scoresheet');

        this.meta.homeTeamId = (j.sheet?.meta?.homeTeamId?._id || j.sheet?.meta?.homeTeamId || '') ?? '';
        this.meta.awayTeamId = (j.sheet?.meta?.awayTeamId?._id || j.sheet?.meta?.awayTeamId || '') ?? '';
        this.meta.season = j.sheet?.meta?.season || CURRENT_SEASON;
        this.meta.league = j.sheet?.meta?.league || 'PDL';

        await Promise.all([this.loadHomeRoster(), this.loadAwayRoster()]);
        this.hydrateFromSheet(j.sheet);
        this.lockFixture = true;
        if (!this.canCurrentUserEdit) {
          this.$store?.toastr?.error?.('You can view this scoresheet, but only admins or players from the fixture teams can edit it.', 3000);
          goToScoresheetView(this.sheetId);
          return;
        }
      } else if (mode === 'add') {
        // ===== /scoresheet/add?fx=:fixtureId =====
        await this.loadFixtureAndLock(fx);
        if (!this.canCurrentUserEdit) {
          this.$store?.toastr?.error?.('You can only create scoresheets for fixtures involving your team.', 3000);
          if (this.meta.fixtureId) {
            const target = `/admin/fixtures/${this.meta.fixtureId}`;
            const router = window.PineconeRouter;
            if (router?.navigate) router.navigate(target);
            else location.assign(target);
          }
          return;
        }
      } else {
        throw new Error('Unrecognized scoresheet route');
      }
    } catch (e) {
      console.error(e);
      this.$store?.toastr?.error?.(e.message || 'Failed to initialize scoresheet');
    } finally {
      this.loading = false;
    }

    this.$nextTick(() => this.updateCompactFixtureBar());
    const onScrollResize = () => {
      this.scoresheetLayoutBump++;
      this.updateCompactFixtureBar();
    };
    window.addEventListener('scroll', onScrollResize, { passive: true });
    window.addEventListener('resize', onScrollResize, { passive: true });
    if (typeof this.$watch === 'function' && this.$store?.ui) {
      this.$watch(() => this.$store.ui.headerCompact, () => { this.scoresheetLayoutBump++; });
    }

    // 5) watchers
    this.$watch('singles', () => {
      this.recalc();
      this.syncBeerPlayersFromSingles();
      this.scheduleAutoSave();
    }, { deep: true });
    this.$watch('doubles', () => {
      this.recalc();
      this.scheduleAutoSave();
    }, { deep: true });
    this.$watch('beer', () => {
      this.recalc();
      this.scheduleAutoSave();
    }, { deep: true });
    this.$watch('meta.date', () => this.scheduleAutoSave());
    this.$watch('upload.url', () => this.scheduleAutoSave());
    this.$watch('upload.key', () => this.scheduleAutoSave());
    this.$watch('forfeit', () => {
      this.onForfeitToggle();
      this.scheduleAutoSave();
    });
    this.$watch('forfeitingTeam', () => {
      this.onForfeitingTeamChange();
      this.scheduleAutoSave();
    });
    this.$watch('forfeitReason', () => this.scheduleAutoSave());
    this.$watch('borrowPicker.open', (open) => this.setBorrowPickerScrollLock(open));
    this.recalc();
  },

  /* -------- API helpers -------- */
  async loadTeams() {
    try {
      const res = await apiFetch('/teams?limit=200&sortBy=name:asc');
      if (res.ok) {
        const j = await res.json();
        this.teams = j.items || j.teams || [];
      }
    } catch (e) { console.error('Teams load failed', e); }
  },

  async loadFixtureAndLock(fixtureId) {
    try {
      // Try /api/fixtures/:id first; fall back to /fixtures/:id if needed
      let res = await apiFetch(`/fixtures/${fixtureId}`);
      if (!res.ok) res = await apiFetch(`/fixtures/${fixtureId}`);
      if (!res.ok) throw new Error('Fixture not found');

      const j = await res.json();
      const fx = j.fixture || j.item || j; // tolerate different shapes

      this.fixture = fx;

      // Expecting: fx.homeTeamId, fx.awayTeamId, fx.date
      this.meta.homeTeamId = fx.homeTeamId?._id || fx.homeTeamId || '';
      this.meta.awayTeamId = fx.awayTeamId?._id || fx.awayTeamId || '';
      this.meta.league = fx.league || '';
      this.meta.season = fx.season || CURRENT_SEASON;

      // console.log(this.meta);

      if (fx.startAt) {
        const d = new Date(fx.startAt);
        const pad = (n) => String(n).padStart(2, '0');
        this.meta.date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        if (this.$refs.dateInput && this.$refs.dateInput._flatpickr) {
          this.$refs.dateInput._flatpickr.setDate(this.meta.date, true);
        }
      }

      // Lock
      this.lockFixture = true;

      // Load rosters for those teams
      await Promise.all([this.loadHomeRoster(), this.loadAwayRoster()]);
    } catch (e) {
      console.error('Fixture load failed', e);
    }
  },
  async loadBorrowablePlayers() {
    try {
      // Include reserves and spare pool (no / other team) as borrow candidates.
      // API caps page size, so load all pages before filtering.
      const allItems = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await apiFetch(`/players?limit=100&page=${page}&sortBy=fullName:asc`);
        if (!res.ok) break;
        const j = await res.json();
        allItems.push(...(j.items || []));
        totalPages = Number(j.totalPages || 1) || 1;
        page += 1;
      } while (page <= totalPages);

      const allowed = new Set(['active', 'reserve', 'pool']);
      this.allPlayers = allItems
        .filter(player => allowed.has(String(player?.status || '').toLowerCase()))
        .map(player => ({
          ...player,
          name: String(player.fullName || '').trim(),
        }));
    } catch (e) {
      console.error('Players load failed', e);
    }
  },
  teamMeta(side) {
    const key = side === 'home' ? 'homeTeamId' : 'awayTeamId';
    const fixtureTeam = this.fixture?.[key];
    if (fixtureTeam && typeof fixtureTeam === 'object') return fixtureTeam;
    const teamId = String(this.meta?.[side === 'home' ? 'homeTeamId' : 'awayTeamId'] || '');
    return this.teams.find((team) => String(team?._id || '') === teamId) || null;
  },
  teamName(side) {
    return this.teamMeta(side)?.name
      || this.fixture?.[side === 'home' ? 'homeTeamName' : 'awayTeamName']
      || (side === 'home' ? 'Home Team' : 'Away Team');
  },
  teamLogo(side) {
    return this.teamMeta(side)?.logoUrl
      || this.fixture?.[side === 'home' ? 'homeTeamLogo' : 'awayTeamLogo']
      || '/assets/placeholder-team.png';
  },
  findPlayerById(playerId, side = '') {
    const id = String(playerId || '');
    if (!id) return null;
    const pools = [];
    if (side && this.roster[side]) pools.push(this.roster[side]);
    if (side === 'home' && this.roster.away) pools.push(this.roster.away);
    if (side === 'away' && this.roster.home) pools.push(this.roster.home);
    pools.push(this.allPlayers || []);
    for (const list of pools) {
      const found = (list || []).find(player => String(player?._id || '') === id);
      if (found) return found;
    }
    return null;
  },
  venueName() {
    return this.fixture?.venueId?.name || this.fixture?.venueName || 'Venue TBC';
  },
  seasonLabel() {
    return this.meta?.season || this.fixture?.season || '';
  },
  leagueLabel() {
    return this.meta?.league || this.fixture?.league || 'PDL';
  },
  playerTeamName(player) {
    if (!player) return '';
    return player.team?.name
      || this.teams.find(team => String(team?._id || '') === String(player.registeredTeamId || ''))?.name
      || '';
  },
  playerBorrowSummary(entry) {
    if (!entry?.borrowed) return '';
    return entry.borrowedFromTeamName ? `Borrowed from ${entry.borrowedFromTeamName}` : 'Borrowed player';
  },
  isBorrowedEntry(entry) {
    return !!entry?.borrowed;
  },
  borrowedPlayerIdsForSide(side) {
    const ids = new Set();
    for (const match of this.singles || []) {
      const entry = match?.[side];
      if (entry?.borrowed && entry?.playerId) ids.add(String(entry.playerId));
    }
    for (const match of this.doubles || []) {
      for (const slot of ['p1', 'p2']) {
        const entry = match?.[side]?.[slot];
        if (entry?.borrowed && entry?.playerId) ids.add(String(entry.playerId));
      }
    }
    return ids;
  },
  rosterOptionLabel(player) {
    if (!player) return '';
    const st = String(player.status || '').toLowerCase();
    if (st === 'reserve') return `${player.name} (reserve)`;
    if (st === 'pool') return `${player.name} (spare pool)`;
    return player.name;
  },
  borrowedEntryName(entry, side = '') {
    if (!entry?.playerId) return 'Borrowed player';
    return this.playerName(entry.playerId, side) || 'Borrowed player';
  },
  cancelBorrowedSelection(target) {
    if (!target) return;
    let entry = null;
    if (target.kind === 'singles') {
      entry = this.singles?.[target.matchIndex]?.[target.side];
    } else if (target.kind === 'doubles') {
      entry = this.doubles?.[target.matchIndex]?.[target.side]?.[target.slot];
    }
    if (!entry) return;
    entry.playerId = '';
    entry.tons = '';
    entry.closes = Array.isArray(entry.closes) ? [] : '';
    this.clearBorrowedEntry(entry);
  },
  borrowPickerTitle() {
    const target = this.borrowPicker?.target;
    if (!target) return 'Borrow Player';
    if (target.kind === 'singles') {
      return `Borrow Player for ${target.side === 'home' ? 'Home' : 'Away'} Singles`;
    }
    const slotLabel = target.slot === 'p1' ? 'Player 1' : 'Player 2';
    return `Borrow Player for ${target.side === 'home' ? 'Home' : 'Away'} Doubles ${slotLabel}`;
  },

  formatDate(dt) {
    if (!dt) return 'Date TBC';
    const d = new Date(dt);
    const datePart = d.toLocaleDateString('en-GB', {
      weekday: 'short', // e.g. Tue
      day: '2-digit',
      month: 'short',   // e.g. Nov
    });
    const timePart = d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${datePart} ${timePart}`; // e.g. Tue, 19 Nov 20:00 -> Tue, 19 Nov 20:00 (no year)
  },

  /* -------- Upload (no Textract) -------- */
  onFileChange(e) {

    this.file = e.target.files?.[0] ?? null;
    if (!this.file) return;

    if (this.useTextract) {
      this.uploadWithTextract();
    } else {
      this.uploadNoTextract();
    }
  },
  async uploadWithTextract() {
    if (!this.file) return;
    this.uploading = true;
    this.scanning = { active: true, step: 'uploading', pct: 10, msg: 'Uploading scoresheet…' };

    try {
      // 1) Upload to S3 via your existing endpoint
      const fd = new FormData();
      fd.append('file', this.file);
      if (this.meta.fixtureId) fd.append('fixtureId', this.meta.fixtureId);
      const res = await apiFetch('/uploads/scoresheet', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Upload failed');
      this.upload.url = data.publicUrl || '';
      this.upload.key = data.key || '';
      this.scanning = { active: true, step: 'queued', pct: 30, msg: 'Starting Textract…' };

      // 2) Build hints (roster + reserves)
      // const homeHints = (this.roster.home || []).map(p => ({ _id: p._id, name: p.name }));
      // const awayHints = (this.roster.away || []).map(p => ({ _id: p._id, name: p.name }));
      // console.log(this.roster.home);
      const homeNames = (this.roster.home || []).map(p => p.name);
      const awayNames = (this.roster.away || []).map(p => p.name);

      // 3) Start OCR
      const start = await apiFetch('/scoresheets/textract/start', {
        method: 'POST',
        body: {
          fixtureId: this.meta.fixtureId,
          uploadKey: this.upload.key,
          hints: {
            homeTeamName: this.fixture.homeTeamName,// this.$store?.teams?.find(t => t._id === this.meta.homeTeamId)?.name || '',
            awayTeamName: this.fixture.awayTeamName, // this.$store?.teams?.find(t => t._id === this.meta.awayTeamId)?.name || '',
            homePlayerNames: homeNames,
            awayPlayerNames: awayNames,
            includePool: true,
          }
        }
      });
      const sj = await start.json();
      if (!start.ok) throw new Error(sj?.message || 'OCR start failed');
      this.ocrJobId = sj.jobId;

      // 4) Poll
      await this.pollOcrUntilDone();
    } catch (err) {
      this.$store?.toastr?.error?.(err.message || 'Upload/OCR failed');
      this.scanning = { active: true, step: 'failed', pct: 100, msg: 'Failed' };
    } finally {
      this.uploading = false;
    }
  },

  async pollOcrUntilDone() {
    this.scanning = { active: true, step: 'analyzing', pct: 55, msg: 'Analyzing with Textract…' };
    const tick = async () => {
      const r = await apiFetch(`/scoresheets/textract/${this.ocrJobId}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j?.message || 'Poll failed');

      if (j.status === 'analyzing') {
        this.scanning.pct = Math.min(this.scanning.pct + 5, 85);
        this.scanning.msg = 'Analyzing with Textract…';
        return false; // keep polling
      }
      if (j.status === 'completed') {
        this.scanning = { active: true, step: 'parsing', pct: 90, msg: 'Parsing and applying…' };
        if (j.patch) this.applyOcrPatch(j.patch);
        this.scanning = { active: true, step: 'completed', pct: 100, msg: 'Auto-filled from scan' };
        this.$store?.toastr?.success?.('Scoresheet populated from scan');
        return true;
      }
      if (j.status === 'failed') {
        this.scanning = { active: true, step: 'failed', pct: 100, msg: j.error || 'OCR failed' };
        this.$store?.toastr?.error?.(j.error || 'Textract failed');
        return true;
      }
      return false;
    };

    // simple poll loop
    for (let i = 0; i < 60; i++) { // ~2 minutes at 2s intervals
      const done = await tick();
      if (done) return;
      await new Promise(res => setTimeout(res, 2000));
    }
    this.$store?.toastr?.error?.('Textract timed out');
    this.scanning = { active: true, step: 'failed', pct: 100, msg: 'Timeout' };
  },
  async uploadNoTextract() {
    if (!this.file) return;
    this.uploading = true;
    try {
      const fd = new FormData();
      fd.append('file', this.file);
      if (this.meta.fixtureId) fd.append('fixtureId', this.meta.fixtureId);
      const res = await apiFetch('/uploads/scoresheet', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Upload failed');
      this.upload.url = data.publicUrl || '';
      this.upload.key = data.key || '';
    } catch (err) {
      alert(err.message || 'Upload failed');
    } finally {
      this.uploading = false;
    }
  },

  applyOcrPatch(patch) {
    // Singles
    if (Array.isArray(patch.singles) && patch.singles.length) {
      // Ensure at least 4 rows
      const n = Math.max(4, patch.singles.length);
      if (this.singles.length < n) {
        this.singles = this.singles.concat(Array.from({ length: n - this.singles.length }).map(buildSinglesMatch));
      }
      patch.singles.forEach((m, i) => {
        if (!this.singles[i]) return;
        if (m.home) {
          if (m.home.playerId) this.singles[i].home.playerId = m.home.playerId;
          if (Number.isFinite(m.home.legs)) this.singles[i].home.legs = m.home.legs;
          if (Array.isArray(m.home.closes)) this.singles[i].home.closes = this.parseCloseArray(m.home.closes, Number(m.home.legs) || 0);
          if (Array.isArray(m.home.tons)) this.singles[i].home.tons = m.home.tons.join(', ');
          if (typeof m.home.borrowed === 'boolean') this.singles[i].home.borrowed = m.home.borrowed;
          if (m.home.borrowedFromTeamId) this.singles[i].home.borrowedFromTeamId = m.home.borrowedFromTeamId;
          if (typeof m.home.borrowedFromTeamName === 'string') this.singles[i].home.borrowedFromTeamName = m.home.borrowedFromTeamName;
        }
        if (m.away) {
          if (m.away.playerId) this.singles[i].away.playerId = m.away.playerId;
          if (Number.isFinite(m.away.legs)) this.singles[i].away.legs = m.away.legs;
          if (Array.isArray(m.away.closes)) this.singles[i].away.closes = this.parseCloseArray(m.away.closes, Number(m.away.legs) || 0);
          if (Array.isArray(m.away.tons)) this.singles[i].away.tons = m.away.tons.join(', ');
          if (typeof m.away.borrowed === 'boolean') this.singles[i].away.borrowed = m.away.borrowed;
          if (m.away.borrowedFromTeamId) this.singles[i].away.borrowedFromTeamId = m.away.borrowedFromTeamId;
          if (typeof m.away.borrowedFromTeamName === 'string') this.singles[i].away.borrowedFromTeamName = m.away.borrowedFromTeamName;
        }
        this.syncSinglesClosings(this.singles[i]);
      });
    }

    // Doubles
    if (Array.isArray(patch.doubles) && patch.doubles.length) {
      const n = Math.max(3, patch.doubles.length);
      if (this.doubles.length < n) {
        this.doubles = this.doubles.concat(Array.from({ length: n - this.doubles.length }).map(buildDoublesMatch));
      }
      patch.doubles.forEach((m, i) => {
        const row = this.doubles[i]; if (!row) return;
        if (m.home) {
          if (Number.isFinite(m.home.legs)) row.home.legs = m.home.legs;
          if (Array.isArray(m.home.players)) {
            const [p1, p2] = m.home.players;
            if (p1?.playerId) row.home.p1.playerId = p1.playerId;
            if (p1?.closes) row.home.p1.closes = this.parseCloseArray(p1.closes);
            if (p1?.tons) row.home.p1.tons = p1.tons.join(', ');
            if (typeof p1?.borrowed === 'boolean') row.home.p1.borrowed = p1.borrowed;
            if (p1?.borrowedFromTeamId) row.home.p1.borrowedFromTeamId = p1.borrowedFromTeamId;
            if (typeof p1?.borrowedFromTeamName === 'string') row.home.p1.borrowedFromTeamName = p1.borrowedFromTeamName;
            if (p2?.playerId) row.home.p2.playerId = p2.playerId;
            if (p2?.closes) row.home.p2.closes = this.parseCloseArray(p2.closes);
            if (p2?.tons) row.home.p2.tons = p2.tons.join(', ');
            if (typeof p2?.borrowed === 'boolean') row.home.p2.borrowed = p2.borrowed;
            if (p2?.borrowedFromTeamId) row.home.p2.borrowedFromTeamId = p2.borrowedFromTeamId;
            if (typeof p2?.borrowedFromTeamName === 'string') row.home.p2.borrowedFromTeamName = p2.borrowedFromTeamName;
          }
        }
        if (m.away) {
          if (Number.isFinite(m.away.legs)) row.away.legs = m.away.legs;
          if (Array.isArray(m.away.players)) {
            const [p1, p2] = m.away.players;
            if (p1?.playerId) row.away.p1.playerId = p1.playerId;
            if (p1?.closes) row.away.p1.closes = this.parseCloseArray(p1.closes);
            if (p1?.tons) row.away.p1.tons = p1.tons.join(', ');
            if (typeof p1?.borrowed === 'boolean') row.away.p1.borrowed = p1.borrowed;
            if (p1?.borrowedFromTeamId) row.away.p1.borrowedFromTeamId = p1.borrowedFromTeamId;
            if (typeof p1?.borrowedFromTeamName === 'string') row.away.p1.borrowedFromTeamName = p1.borrowedFromTeamName;
            if (p2?.playerId) row.away.p2.playerId = p2.playerId;
            if (p2?.closes) row.away.p2.closes = this.parseCloseArray(p2.closes);
            if (p2?.tons) row.away.p2.tons = p2.tons.join(', ');
            if (typeof p2?.borrowed === 'boolean') row.away.p2.borrowed = p2.borrowed;
            if (p2?.borrowedFromTeamId) row.away.p2.borrowedFromTeamId = p2.borrowedFromTeamId;
            if (typeof p2?.borrowedFromTeamName === 'string') row.away.p2.borrowedFromTeamName = p2.borrowedFromTeamName;
          }
        }
        this.syncDoublesClosings(row, 'home');
        this.syncDoublesClosings(row, 'away');
      });
    }

    // Beer
    if (patch.beer) {
      if (patch.beer.winner === 'home' || patch.beer.winner === 'away') this.beer.winner = patch.beer.winner;
      if (Array.isArray(patch.beer.home)) {
        this.beer.homePlayerIds = patch.beer.home.map(p => p.playerId).filter(Boolean).slice(0, 4);
      }
      if (Array.isArray(patch.beer.away)) {
        this.beer.awayPlayerIds = patch.beer.away.map(p => p.playerId).filter(Boolean).slice(0, 4);
      }
    }

    // Clean up invalids if OCR picked anyone not in roster
    this.clearInvalidPicks('home');
    this.clearInvalidPicks('away');
    this.recalc();
  },

  /* -------- Rosters -------- */
  getPlayerRosterOptionText(player) {
    if (!player) return;

    return `${player.name} (${player.status})`

  },
  clearBorrowedEntry(entry) {
    if (!entry) return;
    entry.borrowed = false;
    entry.borrowedFromTeamId = '';
    entry.borrowedFromTeamName = '';
  },
  syncBorrowedMetaForEntry(entry, side) {
    if (!entry?.playerId) {
      this.clearBorrowedEntry(entry);
      return;
    }
    const rosterIds = new Set((this.roster[side] || []).map(player => String(player._id || '')));
    if (rosterIds.has(String(entry.playerId))) {
      this.clearBorrowedEntry(entry);
    }
  },
  onSinglesPlayerSelected(match, side) {
    this.syncBorrowedMetaForEntry(match?.[side], side);
  },
  onDoublesPlayerSelected(match, side, slot) {
    this.syncBorrowedMetaForEntry(match?.[side]?.[slot], side);
    this.dedupeDoubles(match, side, slot);
  },
  setBorrowPickerScrollLock(locked) {
    const shouldLock = !!locked;
    try {
      document.body.style.overflow = shouldLock ? 'hidden' : '';
      document.documentElement.style.overflow = shouldLock ? 'hidden' : '';
      document.body.style.touchAction = shouldLock ? 'none' : '';
    } catch { }
  },
  openBorrowPicker(target) {
    this.borrowPicker.target = target;
    this.borrowPicker.side = target?.side || 'home';
    this.borrowPicker.search = '';
    this.borrowPicker.open = true;
    this.setBorrowPickerScrollLock(true);
  },
  closeBorrowPicker() {
    this.borrowPicker.open = false;
    this.borrowPicker.search = '';
    this.borrowPicker.target = null;
    this.setBorrowPickerScrollLock(false);
  },
  borrowedPlayerCandidates() {
    const search = String(this.borrowPicker.search || '').trim().toLowerCase();
    const side = this.borrowPicker?.side || 'home';
    const oppositeSide = side === 'home' ? 'away' : 'home';
    const oppositeBorrowedIds = this.borrowedPlayerIdsForSide(oppositeSide);
    return (this.allPlayers || [])
      .filter(player => {
        const playerId = String(player?._id || '');
        return playerId && !oppositeBorrowedIds.has(playerId);
      })
      .filter(player => {
        if (!search) return true;
        const haystack = `${player.fullName || ''} ${player.nickname || ''} ${this.playerTeamName(player)}${String(player?.status || '').toLowerCase() === 'pool' ? ' spare pool' : ''}`.toLowerCase();
        return haystack.includes(search);
      })
      .slice(0, 50);
  },
  assignBorrowedPlayer(player) {
    const target = this.borrowPicker.target;
    if (!target || !player?._id) return;
    const side = target.side;
    const oppositeSide = side === 'home' ? 'away' : 'home';
    const playerId = String(player._id);
    if (this.borrowedPlayerIdsForSide(oppositeSide).has(playerId)) {
      this.$store?.toastr?.error?.('That borrowed player is already assigned to the opposite side.', 3000);
      return;
    }
    const teamName = String(player?.status || '').toLowerCase() === 'pool'
      ? 'Spare pool'
      : this.playerTeamName(player);
    if (target.kind === 'singles') {
      const entry = this.singles?.[target.matchIndex]?.[side];
      if (!entry) return;
      entry.playerId = playerId;
      entry.borrowed = true;
      entry.borrowedFromTeamId = String(player.registeredTeamId || '');
      entry.borrowedFromTeamName = teamName;
    }
    if (target.kind === 'doubles') {
      const entry = this.doubles?.[target.matchIndex]?.[side]?.[target.slot];
      if (!entry) return;
      entry.playerId = playerId;
      entry.borrowed = true;
      entry.borrowedFromTeamId = String(player.registeredTeamId || '');
      entry.borrowedFromTeamName = teamName;
      this.dedupeDoubles(this.doubles?.[target.matchIndex], side, target.slot);
    }
    this.closeBorrowPicker();
  },
  async loadHomeRoster() {
    this.roster.home = await this.fetchRoster(this.meta.homeTeamId);
    this.clearInvalidPicks('home');
  },
  async loadAwayRoster() {
    this.roster.away = await this.fetchRoster(this.meta.awayTeamId);
    this.clearInvalidPicks('away');
  },
  async fetchRoster(teamId) {
    if (!teamId) return [];
    try {
      const res = await apiFetch(`/teams/${teamId}/roster`);
      const j = await res.json();
      const rostered = (j.roster || []).map(p => ({
        _id: p._id,
        status: p.status,
        name: p.fullName.trim()
      }));

      const reserves = (j.reserves || []).map(p => ({
        _id: p._id,
        status: p.status,
        name: p.fullName.trim()
      }));

      return [...rostered, ...reserves];

    } catch { return []; }
  },
  clearInvalidPicks(side) {
    const ids = new Set([
      ...this.roster[side].map(p => p._id),
      ...(this.allPlayers || []).map(p => p._id),
    ]);
    // singles
    this.singles.forEach(m => {
      if (m[side].playerId && !ids.has(m[side].playerId)) {
        m[side].playerId = '';
        this.clearBorrowedEntry(m[side]);
      }
    });
    // doubles
    this.doubles.forEach(m => {
      ['p1', 'p2'].forEach(k => {
        if (m[side][k].playerId && !ids.has(m[side][k].playerId)) {
          m[side][k].playerId = '';
          this.clearBorrowedEntry(m[side][k]);
        }
      });
    });
    // beer
    const key = side + 'PlayerIds';
    this.beer[key] = this.beer[key].filter(pid => ids.has(pid)).slice(0, 4);
  },
  sideBeerPlayerIds(side) {
    const out = [];
    const seen = new Set();
    for (const match of this.singles) {
      const id = match?.[side]?.playerId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    for (const match of this.doubles) {
      const p1 = match?.[side]?.p1?.playerId;
      const p2 = match?.[side]?.p2?.playerId;
      if (p1 && !seen.has(p1)) {
        seen.add(p1);
        out.push(p1);
      }
      if (p2 && !seen.has(p2)) {
        seen.add(p2);
        out.push(p2);
      }
    }
    return out.slice(0, 4);
  },
  beerEligiblePlayers(side) {
    return this.sideBeerPlayerIds(side)
      .map(id => this.findPlayerById(id, side))
      .filter(Boolean);
  },
  hasBeerEligiblePlayers() {
    return this.beerEligiblePlayers('home').length > 0 || this.beerEligiblePlayers('away').length > 0;
  },
  playerName(pid, side) {
    const p = this.findPlayerById(pid, side);
    if (!p) return pid;
    const base = String(p.name || p.fullName || '').trim() || pid;
    const st = String(p?.status || '').toLowerCase();
    if (st === 'reserve') return `${base} (reserve)`;
    if (st === 'pool') return `${base} (spare pool)`;
    return base;
  },

  borrowCandidateLabel(player) {
    if (!player) return '';
    const base = String(player.name || player.fullName || player.nickname || '').trim();
    if (!base) return '';
    const st = String(player?.status || '').toLowerCase();
    if (st === 'reserve') return `${base} (reserve)`;
    if (st === 'pool') return `${base} (spare pool)`;
    return base;
  },

  borrowCandidateTeamLine(player) {
    if (!player) return 'Other team';
    if (String(player.status || '').toLowerCase() === 'pool') return 'Spare pool';
    return this.playerTeamName(player) || 'Other team';
  },
  syncBeerPlayersFromSingles() {
    // Kept method name for compatibility with existing callers.
    this.beer.homePlayerIds = this.sideBeerPlayerIds('home');
    this.beer.awayPlayerIds = this.sideBeerPlayerIds('away');
  },
  isBeerBorrowedPlayer(pid, side) {
    const id = String(pid || '');
    if (!id) return false;
    for (const match of this.singles || []) {
      const entry = match?.[side];
      if (String(entry?.playerId || '') === id && !!entry?.borrowed) return true;
    }
    for (const match of this.doubles || []) {
      for (const slot of ['p1', 'p2']) {
        const entry = match?.[side]?.[slot];
        if (String(entry?.playerId || '') === id && !!entry?.borrowed) return true;
      }
    }
    return false;
  },
  beerBorrowedLabel(pid, side) {
    const id = String(pid || '');
    if (!id) return '';
    for (const match of this.singles || []) {
      const entry = match?.[side];
      if (String(entry?.playerId || '') === id && !!entry?.borrowed) {
        return entry.borrowedFromTeamName ? `Borrowed from ${entry.borrowedFromTeamName}` : 'Borrowed player';
      }
    }
    for (const match of this.doubles || []) {
      for (const slot of ['p1', 'p2']) {
        const entry = match?.[side]?.[slot];
        if (String(entry?.playerId || '') === id && !!entry?.borrowed) {
          return entry.borrowedFromTeamName ? `Borrowed from ${entry.borrowedFromTeamName}` : 'Borrowed player';
        }
      }
    }
    return '';
  },
  limitBeer(side) {
    const key = side + 'PlayerIds';
    const eligible = new Set(this.sideBeerPlayerIds(side));
    this.beer[key] = this.beer[key].filter(pid => eligible.has(pid)).slice(0, 4);
    // User has made a manual change – stop auto-filling from Singles
    this.beer.autoFromSingles = false;
  },
  maybeUpdateBeerFromSingles() {
    this.syncBeerEligiblePlayers();
    // If the user has manually tweaked Beer players, don't auto-overwrite.
    if (!this.beer.autoFromSingles) return;
    this.beer.homePlayerIds = this.sideBeerPlayerIds('home');
    this.beer.awayPlayerIds = this.sideBeerPlayerIds('away');
  },
  dedupeDoubles(m, side, preferredSlot = 'p1') {
    const a = m?.[side]?.p1?.playerId;
    const b = m?.[side]?.p2?.playerId;
    if (!(a && b && a === b)) return;
    const slotToClear = preferredSlot === 'p2' ? 'p1' : 'p2';
    m[side][slotToClear].playerId = '';
    this.clearBorrowedEntry(m[side][slotToClear]);
  },
  isDoublesPlayerOptionDisabled(match, side, slot, playerId) {
    if (!playerId) return false;
    const otherSlot = slot === 'p1' ? 'p2' : 'p1';
    return match?.[side]?.[otherSlot]?.playerId === playerId;
  },

  /* -------- Totals -------- */
  recalc() {
    const sumLegs = (arr, side) => arr.reduce((acc, m) => acc + (Number(m?.[side]?.legs) || 0), 0);
    this.totals.singles.home = sumLegs(this.singles, 'home');
    this.totals.singles.away = sumLegs(this.singles, 'away');
    this.totals.doubles.home = this.doubles.reduce((a, m) => a + (Number(m.home.legs) || 0), 0);
    this.totals.doubles.away = this.doubles.reduce((a, m) => a + (Number(m.away.legs) || 0), 0);
  },
  grandTotal() {
    const beerHome = this.beer.winner === 'home' ? 1 : 0;
    const beerAway = this.beer.winner === 'away' ? 1 : 0;
    return {
      home: this.totals.singles.home + this.totals.doubles.home + beerHome,
      away: this.totals.singles.away + this.totals.doubles.away + beerAway
    };
  },
  /** Photo tab: league-style points (2 for a win, 1 each if drawn on total legs). */
  photoSectionMatchSummary() {
    const g = this.grandTotal();
    const h = g.home;
    const a = g.away;
    const homeName = this.teamName('home');
    const awayName = this.teamName('away');
    if (h > a) return { outcome: 'home', winnerName: homeName, winnerPoints: 2, loserName: awayName };
    if (a > h) return { outcome: 'away', winnerName: awayName, winnerPoints: 2, loserName: homeName };
    if (h === a && (h > 0 || a > 0)) return { outcome: 'draw', homeName, awayName, pointsEach: 1 };
    return { outcome: 'none', homeName, awayName };
  },
  photoSectionWinnerBannerClass() {
    const s = this.photoSectionMatchSummary();
    if (s.outcome === 'home') return 'border-sky-300 bg-sky-50/90 dark:border-sky-800 dark:bg-sky-950/35';
    if (s.outcome === 'away') return 'border-rose-300 bg-rose-50/90 dark:border-rose-900 dark:bg-rose-950/35';
    if (s.outcome === 'draw') return 'border-amber-200 bg-amber-50/80 dark:border-amber-900/50 dark:bg-amber-950/25';
    return 'border-neutral-200 bg-neutral-50/80 dark:border-neutral-700 dark:bg-neutral-900/50';
  },

  /* -------- Submit payload -------- */
  snapshotPayload(status = 'draft') {

    const singlesSrc = status === 'submitted' ? this.singles.slice(0, this.requiredSinglesCount) : this.singles;
    const doublesSrc = status === 'submitted' ? this.doubles.slice(0, this.requiredDoublesCount) : this.doubles;

    const singles = singlesSrc.map(m => ({
      home: {
        playerId: m.home.playerId || null,
        legs: Number(m.home.legs) || 0,
        closes: this.parseCloseArray(m.home.closes),
        tons: this.parseCSV(m.home.tons),
        borrowed: !!m.home.borrowed,
        borrowedFromTeamId: m.home.borrowedFromTeamId || null,
        borrowedFromTeamName: m.home.borrowedFromTeamName || ''
      },
      away: {
        playerId: m.away.playerId || null,
        legs: Number(m.away.legs) || 0,
        closes: this.parseCloseArray(m.away.closes),
        tons: this.parseCSV(m.away.tons),
        borrowed: !!m.away.borrowed,
        borrowedFromTeamId: m.away.borrowedFromTeamId || null,
        borrowedFromTeamName: m.away.borrowedFromTeamName || ''
      }
    }));

    const doubles = doublesSrc.map(m => ({
      home: {
        legs: Number(m.home.legs) || 0,
        players: [
          { playerId: m.home.p1.playerId || null, closes: this.parseCloseArray(m.home.p1.closes), tons: this.parseCSV(m.home.p1.tons), borrowed: !!m.home.p1.borrowed, borrowedFromTeamId: m.home.p1.borrowedFromTeamId || null, borrowedFromTeamName: m.home.p1.borrowedFromTeamName || '' },
          { playerId: m.home.p2.playerId || null, closes: this.parseCloseArray(m.home.p2.closes), tons: this.parseCSV(m.home.p2.tons), borrowed: !!m.home.p2.borrowed, borrowedFromTeamId: m.home.p2.borrowedFromTeamId || null, borrowedFromTeamName: m.home.p2.borrowedFromTeamName || '' }
        ]
      },
      away: {
        legs: Number(m.away.legs) || 0,
        players: [
          { playerId: m.away.p1.playerId || null, closes: this.parseCloseArray(m.away.p1.closes), tons: this.parseCSV(m.away.p1.tons), borrowed: !!m.away.p1.borrowed, borrowedFromTeamId: m.away.p1.borrowedFromTeamId || null, borrowedFromTeamName: m.away.p1.borrowedFromTeamName || '' },
          { playerId: m.away.p2.playerId || null, closes: this.parseCloseArray(m.away.p2.closes), tons: this.parseCSV(m.away.p2.tons), borrowed: !!m.away.p2.borrowed, borrowedFromTeamId: m.away.p2.borrowedFromTeamId || null, borrowedFromTeamName: m.away.p2.borrowedFromTeamName || '' }
        ]
      }
    }));

    const beer = {
      winner: this.beer.winner || null,
      home: this.beer.homePlayerIds.map(pid => ({
        playerId: pid,
        closes: this.parseCSV(this.beer.homeCloses[pid]),
        tons: this.parseCSV(this.beer.homeTons[pid])
      })),
      away: this.beer.awayPlayerIds.map(pid => ({
        playerId: pid,
        closes: this.parseCSV(this.beer.awayCloses[pid]),
        tons: this.parseCSV(this.beer.awayTons[pid])
      }))
    };

    const grand = this.grandTotal();
    return {
      status,
      adminOverride: !!this.adminOverride,
      fixtureId: this.meta.fixtureId,   // required
      forfeit: !!this.forfeit,
      forfeitingTeam: this.forfeit && (this.forfeitingTeam === 'home' || this.forfeitingTeam === 'away')
        ? this.forfeitingTeam
        : null,
      forfeitReason: this.forfeit ? String(this.forfeitReason || '').trim().slice(0, 2000) : '',
      meta: {
        date: this.meta.date,
        round: this.meta.round,
        uploadKey: this.forfeit ? null : (this.upload.key || null),
        uploadUrl: this.forfeit ? null : (this.upload.url || null),
        homeTeamId: this.meta.homeTeamId || undefined,
        awayTeamId: this.meta.awayTeamId || undefined,
        season: this.meta.season || '',
        league: this.meta.league || 'PDL',
      },
      singles, doubles, beer,
      totals: {
        singles: this.totals.singles,
        doubles: this.totals.doubles,
        beer: beer.winner === 'home' ? [1, 0] : beer.winner === 'away' ? [0, 1] : [0, 0],
        match: [grand.home, grand.away]
      }
    };
  },
  currentDraftSignature() {
    return JSON.stringify(this.snapshotPayload('draft'));
  },
  markCurrentDraftAsSaved() {
    this.lastSavedDraftSignature = this.currentDraftSignature();
  },
  canAutoSaveDraft() {
    return this.autoSaveEnabled
      && !this.loading
      && !this.saving
      && !this.submitting
      && !this.approving
      && !this.isLocked
      && !this.isSubmitted
      && !this.isApproved;
  },
  clearAutoSaveTimer() {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  },
  scheduleAutoSave() {
    if (!this.canAutoSaveDraft()) return;
    this.clearAutoSaveTimer();
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      this.runAutoSave();
    }, this.autoSaveDelayMs);
  },
  async runAutoSave() {
    if (!this.canAutoSaveDraft()) return;
    const signature = this.currentDraftSignature();
    if (signature === this.lastSavedDraftSignature) return;
    this.autoSaving = true;
    try {
      await this.saveDraft({ silent: true });
    } finally {
      this.autoSaving = false;
    }
  },

  /* -------- Actions -------- */
  async saveDraft(options = {}) {
    const { silent = false } = options;
    if (this.saving) return;
    this.saving = true;
    this.clearAutoSaveTimer();
    try {
      const wasNew = !this.sheetId;

      // When admin unlocks an approved sheet, saving should move it back to draft
      // so it can be re-submitted for review.
      const desiredStatus = 'draft';

      const payload = this.snapshotPayload(desiredStatus);

      // If we already have a sheet, PATCH it; otherwise POST a new one
      const url = this.sheetId ? `/scoresheets/${this.sheetId}` : '/scoresheets';
      const method = this.sheetId ? 'PATCH' : 'POST';

      const res = await apiFetch(url, { method, body: payload });
      const j = await res.json();
      if (!res.ok) return alert(j?.message || 'Failed to save draft');

      this.sheetId = String(j.id || this.sheetId || '');
      if (!this.sheetId) {
        // nothing
      }

      this.isSubmitted = (j.status || '').toLowerCase() === 'submitted';
      this.isApproved = (j.status || '').toLowerCase() === 'approved';
      this.sheetId = String(j.id || this.sheetId || '');

      if (wasNew && this.sheetId) {
        const routePrefix = String(this.routePrefix || parseRoute()?.prefix || '/admin');
        removeFxFromUrl(this.sheetId, routePrefix, this.sectionTab);
      }

      this.markCurrentDraftAsSaved();
      if (!silent) this.$store.toastr.success('Draft saved.');
    } catch (err) {
      this.$store.toastr.error(err.message || 'Failed to save draft');
    } finally {
      this.saving = false;
    }
  },

  async submitForReview() {
    if (this.submitting) return;

    // ✅ Validate first
    const { ok, errors } = this.validateForSubmit();
    if (!ok) {
      // show first few errors to the user
      errors.slice(0, 5).forEach((e, i) => this.$store?.toastr?.error?.(i === 0 ? `Fix required: ${e}` : e, 3000));
      return;
    }

    this.submitting = true;
    try {
      let res, j;
      const payload = this.snapshotPayload('submitted');

      if (this.sheetId) {
        res = await apiFetch(`/scoresheets/${this.sheetId}`, { method: 'PATCH', body: payload });
        j = await res.json();
        if (!res.ok) throw new Error(j?.message || 'Submit failed');
      } else {
        res = await apiFetch('/scoresheets/submit', { method: 'POST', body: payload });
        j = await res.json();
        if (!res.ok) throw new Error(j?.message || 'Submit failed');

        if (!this.sheetId && j.id) {
          this.sheetId = String(j.id);
          const routePrefix = String(this.routePrefix || parseRoute()?.prefix || '/admin');
          removeFxFromUrl(this.sheetId, routePrefix, this.sectionTab);
        }

      }

      this.isSubmitted = true;
      this.isApproved = (j.status || '').toLowerCase() === 'approved';
      this.markCurrentDraftAsSaved();
      this.$store.toastr.success('Submitted for review. Thanks!');
      goToScoresheetView(this.sheetId || j.id);
    } catch (err) {
      this.$store.toastr.error(err.message || 'Submit failed');
    } finally {
      this.submitting = false;
    }
  },

  async approveSheet() {
    if (this.approving) return;
    if (!this.sheetId) return this.$store.toastr.error('No scoresheet loaded.');
    if (!this.isSubmitted) return this.$store.toastr.error('Only submitted sheets can be approved.');

    // ✅ Validate again before finalizing
    const { ok, errors } = this.validateForSubmit();
    if (!ok) {
      errors.slice(0, 5).forEach((e, i) => this.$store?.toastr?.error?.(i === 0 ? `Cannot approve: ${e}` : e, 3000));
      return;
    }

    if (!confirm('Approve this scoresheet? This will finalize stats and the fixture.')) return;

    this.approving = true;
    try {
      const res = await apiFetch(`/scoresheets/${this.sheetId}/approve`, { method: 'PATCH' });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.message || 'Approval failed');

      this.isApproved = true;
      this.isSubmitted = false; // once approved, it’s no longer “awaiting review”
      this.$store.toastr.success('Scoresheet approved.');
    } catch (err) {
      this.$store.toastr.error(err.message || 'Approval failed');
    } finally {
      this.approving = false;
    }
  },
  adminUnlock() {
    // admin chooses to edit an approved sheet
    this.adminOverride = true;
    this.$store?.toastr?.info?.('Override enabled. You can edit this approved scoresheet.');
  },

  adminCancelOverride() {
    this.adminOverride = false;
    this.$store?.toastr?.info?.('Override cancelled. Sheet is locked again.');
  },


})); 

Alpine.data('scoresheetView', () => ({
  sheetId: '',
  loading: true,
  error: '',
  sheet: null,
  lightboxOpen: false,
  shareOpen: false,

  init() {
    this.sheetId = this.readRouteId();
    if (!this.sheetId) {
      this.error = 'Missing scoresheet id';
      this.loading = false;
      return;
    }
    this.load();
  },

  readRouteId() {
    const parts = location.pathname.split('/').filter(Boolean);
    const i = parts.lastIndexOf('scoresheet');
    return i >= 0 ? (parts[i + 1] || '') : '';
  },

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const res = await apiFetch(`/scoresheets/${this.sheetId}?populate=true`);
      const json = await res.json();
      if (!res.ok || !json?.sheet) {
        throw new Error(json?.message || 'Failed to load scoresheet');
      }
      this.sheet = json.sheet;
    } catch (err) {
      console.error(err);
      this.error = err.message || 'Failed to load scoresheet';
    } finally {
      this.loading = false;
    }
  },

  statusLabel(status) {
    if (status === 'approved') return 'Approved';
    if (status === 'submitted') return 'Submitted';
    if (status === 'void') return 'Voided';
    return 'Draft';
  },

  statusClass(status) {
    if (status === 'approved') return 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800';
    if (status === 'submitted') return 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800';
    if (status === 'void') return 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-800';
    return 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800';
  },

  team(side) {
    return this.sheet?.meta?.[`${side}TeamId`] || null;
  },

  teamName(side) {
    return this.team(side)?.name || (side === 'home' ? 'Home Team' : 'Away Team');
  },

  teamLogo(side) {
    return this.team(side)?.logoUrl || '/assets/placeholder-team.png';
  },

  listHref() {
    const fid = this._validId(this.meta.fixtureId) ? String(this.meta.fixtureId).trim() : '';
    if (fid) return `/admin/fixtures/${encodeURIComponent(fid)}`;
    return '/admin/scoresheet';
  },

  listBackLabel() {
    return this._validId(this.meta.fixtureId) ? 'Fixture' : 'Scoresheets';
  },

  fixtureStartAt() {
    return this.sheet?.fixtureId?.startAt || null;
  },

  venueName() {
    return this.sheet?.fixtureId?.venueId?.name || this.sheet?.fixtureId?.venueName || 'Venue TBC';
  },

  roundLabel() {
    return this.sheet?.fixtureId?.roundLabel || this.sheet?.meta?.round || 'Round TBC';
  },

  seasonLabel() {
    return this.sheet?.meta?.season || this.sheet?.fixtureId?.season || '';
  },

  leagueLabel() {
    return this.sheet?.meta?.league || this.sheet?.fixtureId?.league || 'PDL';
  },

  formatDate(dt) {
    if (!dt) return 'Date TBC';
    const d = new Date(dt);
    return d.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  },

  playerName(player) {
    if (!player) return 'TBC';
    return player.nickname ? `${player.fullName} (${player.nickname})` : player.fullName || 'TBC';
  },

  playerImage(player) {
    return player?.imageUrl || '/assets/placeholder-player.png';
  },

  arrayOrEmpty(value) {
    return Array.isArray(value) ? value.filter(Boolean) : [];
  },

  checkoutValue(raw) {
    const match = String(raw ?? '').match(/\d+/);
    const value = match ? Number(match[0]) : NaN;
    return Number.isFinite(value) ? value : 0;
  },

  scoreValue(raw) {
    const match = String(raw ?? '').match(/\d+/);
    const value = match ? Number(match[0]) : NaN;
    return Number.isFinite(value) ? value : 0;
  },

  scoreList(value, emptyLabel = 'None') {
    const items = this.arrayOrEmpty(value);
    return items.length ? items.join(', ') : emptyLabel;
  },

  legsLabel(legs) {
    const count = Number(legs) || 0;
    return `${count} leg${count === 1 ? '' : 's'}`;
  },

  playerResultSummary(player, legs) {
    return {
      legs: this.legsLabel(legs),
      closes: this.scoreList(player?.closes),
      tons: this.scoreList(player?.tons),
    };
  },

  matchWinnerClass(homeLegs, awayLegs, side) {
    const isWinner = side === 'home' ? homeLegs > awayLegs : awayLegs > homeLegs;
    return isWinner
      ? 'border-emerald-300 bg-emerald-50/80 dark:border-emerald-800 dark:bg-emerald-950/20'
      : 'border-neutral-200 bg-white/70 dark:border-neutral-800 dark:bg-neutral-900/40';
  },

  beerPlayers(side) {
    return this.arrayOrEmpty(this.sheet?.beer?.[side]);
  },

  beerWinnerLabel() {
    const winner = this.sheet?.beer?.winner;
    if (winner === 'home') return this.teamName('home');
    if (winner === 'away') return this.teamName('away');
    return 'Not recorded';
  },

  total(side) {
    return Number(this.sheet?.totals?.match?.[side === 'home' ? 0 : 1]) || 0;
  },

  isShareable() {
    return ['submitted', 'approved'].includes(String(this.sheet?.status || ''));
  },

  shareImageUrl() {
    if (this.sheet?.forfeit) return '';
    return this.sheet?.meta?.uploadUrl || '';
  },

  forfeitingTeamLabel() {
    const ft = this.sheet?.forfeitingTeam;
    if (ft === 'home') return `${this.teamName('home')} forfeited`;
    if (ft === 'away') return `${this.teamName('away')} forfeited`;
    return '';
  },

  forfeitReasonText() {
    return String(this.sheet?.forfeitReason || '').trim();
  },

  shareResultLine() {
    return `${this.teamName('home')} ${this.total('home')} - ${this.total('away')} ${this.teamName('away')}`;
  },

  shareHighlightLine(label, summary, emptyLabel) {
    if (!summary?.player) return `${label}: ${emptyLabel}`;
    return `${label}: ${this.playerName(summary.player)} with ${summary.label || summary.tons}`;
  },

  sharePostText() {
    if (this.sheet?.forfeit) {
      const lines = [
        'Phuket Darts League Result',
        '',
        'This match was recorded as a forfeit.',
        this.forfeitingTeamLabel(),
        `Reason: ${this.forfeitReasonText()}`,
        '',
        this.shareResultLine(),
        `Venue: ${this.venueName()}`,
        `Date: ${this.formatDate(this.fixtureStartAt())}`,
        '',
        'League points are awarded as usual for the non-forfeiting team.',
        '',
        '#PhuketDartsLeague #DartsNight #LeagueDarts',
      ];
      return lines.join('\n');
    }
    const lines = [
      'Phuket Darts League Result',
      '',
      "Tonight's match is in the books.",
      '',
      this.shareResultLine(),
      `Venue: ${this.venueName()}`,
      `Date: ${this.formatDate(this.fixtureStartAt())}`,
      '',
      'Top moments:',
      this.shareHighlightLine('Highest checkout', this.highestCheckoutSummary(), 'No checkout recorded'),
      this.shareHighlightLine('Highest score', this.highestScoreSummary(), 'No score recorded'),
      this.mostTonsSummary()?.player
        ? `Most tons: ${this.playerName(this.mostTonsSummary().player)} with ${this.mostTonsSummary().tons}`
        : 'Most tons: No tons recorded',
      '',
      'Great darts from both teams. Thanks to everyone who played and supported tonight.',
    ];
    if (this.shareImageUrl()) {
      lines.push('', `Scoresheet image: ${this.shareImageUrl()}`);
    }
    lines.push('', '#PhuketDartsLeague #DartsNight #LeagueDarts');
    return lines.join('\n');
  },

  openShare() {
    if (!this.isShareable()) return;
    this.shareOpen = true;
    document.body.classList.add('overflow-hidden');
  },

  closeShare() {
    this.shareOpen = false;
    if (!this.lightboxOpen) document.body.classList.remove('overflow-hidden');
  },

  async copyShareText() {
    const text = this.sharePostText();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const input = document.createElement('textarea');
        input.value = text;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      this.$store?.toastr?.success?.('Facebook post copied.');
    } catch (err) {
      this.$store?.toastr?.error?.(err?.message || 'Copy failed');
    }
  },

  allCloseValues() {
    if (!this.sheet) return [];
    const values = [];
    for (const match of this.sheet.singles || []) {
      values.push(...this.arrayOrEmpty(match?.home?.closes));
      values.push(...this.arrayOrEmpty(match?.away?.closes));
    }
    for (const match of this.sheet.doubles || []) {
      for (const player of this.arrayOrEmpty(match?.home?.players)) values.push(...this.arrayOrEmpty(player?.closes));
      for (const player of this.arrayOrEmpty(match?.away?.players)) values.push(...this.arrayOrEmpty(player?.closes));
    }
    for (const player of this.beerPlayers('home')) values.push(...this.arrayOrEmpty(player?.closes));
    for (const player of this.beerPlayers('away')) values.push(...this.arrayOrEmpty(player?.closes));
    return values.map((raw) => this.checkoutValue(raw)).filter((value) => value > 0);
  },

  totalTons() {
    if (!this.sheet) return 0;
    let count = 0;
    for (const match of this.sheet.singles || []) {
      count += this.arrayOrEmpty(match?.home?.tons).length;
      count += this.arrayOrEmpty(match?.away?.tons).length;
    }
    for (const match of this.sheet.doubles || []) {
      for (const player of this.arrayOrEmpty(match?.home?.players)) count += this.arrayOrEmpty(player?.tons).length;
      for (const player of this.arrayOrEmpty(match?.away?.players)) count += this.arrayOrEmpty(player?.tons).length;
    }
    for (const player of this.beerPlayers('home')) count += this.arrayOrEmpty(player?.tons).length;
    for (const player of this.beerPlayers('away')) count += this.arrayOrEmpty(player?.tons).length;
    return count;
  },

  highestCheckout() {
    const values = this.allCloseValues();
    return values.length ? Math.max(...values) : 0;
  },

  totalCheckouts() {
    return this.allCloseValues().length;
  },

  highestCheckoutSummary() {
    if (!this.sheet) return null;
    let best = null;
    const apply = (player, side, closes = []) => {
      for (const close of this.arrayOrEmpty(closes)) {
        const value = this.checkoutValue(close);
        if (!value) continue;
        if (!best || value > best.value) {
          best = {
            value,
            label: String(close),
            player,
            side,
          };
        }
      }
    };

    for (const match of this.sheet.singles || []) {
      apply(match?.home?.playerId, 'home', match?.home?.closes);
      apply(match?.away?.playerId, 'away', match?.away?.closes);
    }
    for (const match of this.sheet.doubles || []) {
      for (const player of this.arrayOrEmpty(match?.home?.players)) apply(player?.playerId, 'home', player?.closes);
      for (const player of this.arrayOrEmpty(match?.away?.players)) apply(player?.playerId, 'away', player?.closes);
    }
    for (const player of this.beerPlayers('home')) apply(player?.playerId, 'home', player?.closes);
    for (const player of this.beerPlayers('away')) apply(player?.playerId, 'away', player?.closes);

    return best;
  },

  highestScoreSummary() {
    if (!this.sheet) return null;
    let best = null;
    const apply = (player, side, tons = []) => {
      for (const score of this.arrayOrEmpty(tons)) {
        const value = this.scoreValue(score);
        if (!value) continue;
        if (!best || value > best.value) {
          best = {
            value,
            label: String(score),
            player,
            side,
          };
        }
      }
    };

    for (const match of this.sheet.singles || []) {
      apply(match?.home?.playerId, 'home', match?.home?.tons);
      apply(match?.away?.playerId, 'away', match?.away?.tons);
    }
    for (const match of this.sheet.doubles || []) {
      for (const player of this.arrayOrEmpty(match?.home?.players)) apply(player?.playerId, 'home', player?.tons);
      for (const player of this.arrayOrEmpty(match?.away?.players)) apply(player?.playerId, 'away', player?.tons);
    }
    for (const player of this.beerPlayers('home')) apply(player?.playerId, 'home', player?.tons);
    for (const player of this.beerPlayers('away')) apply(player?.playerId, 'away', player?.tons);

    return best;
  },

  mostTonsSummary() {
    const summaries = this.playerSummaries();
    if (!summaries.length) return null;
    const [best] = [...summaries].sort((a, b) =>
      (b.tons - a.tons) ||
      ((Math.max(...b.tonValues.map((score) => this.scoreValue(score)), 0)) - (Math.max(...a.tonValues.map((score) => this.scoreValue(score)), 0))) ||
      a.name.localeCompare(b.name)
    );
    return best?.tons ? best : null;
  },

  playerSummaries() {
    if (!this.sheet) return [];
    const rows = new Map();
    const ensure = (player, side) => {
      const id = player?._id || player;
      if (!id) return null;
      const key = String(id);
      if (!rows.has(key)) {
        rows.set(key, {
          id: key,
          player,
          side,
          name: this.playerName(player),
          entries: 0,
          legsWon: 0,
          tons: 0,
          closes: 0,
          highestCheckout: 0,
          tonValues: [],
          closeValues: [],
        });
      }
      return rows.get(key);
    };

    const apply = (player, side, legsWon, tons = [], closes = []) => {
      const row = ensure(player, side);
      if (!row) return;
      row.entries += 1;
      row.legsWon += Number(legsWon) || 0;
      row.tons += this.arrayOrEmpty(tons).length;
      row.closes += this.arrayOrEmpty(closes).length;
      row.tonValues.push(...this.arrayOrEmpty(tons).map(String));
      row.closeValues.push(...this.arrayOrEmpty(closes).map(String));
      for (const close of this.arrayOrEmpty(closes)) {
        row.highestCheckout = Math.max(row.highestCheckout, this.checkoutValue(close));
      }
    };

    for (const match of this.sheet.singles || []) {
      apply(match?.home?.playerId, 'home', match?.home?.legs, match?.home?.tons, match?.home?.closes);
      apply(match?.away?.playerId, 'away', match?.away?.legs, match?.away?.tons, match?.away?.closes);
    }
    for (const match of this.sheet.doubles || []) {
      for (const player of this.arrayOrEmpty(match?.home?.players)) apply(player?.playerId, 'home', match?.home?.legs, player?.tons, player?.closes);
      for (const player of this.arrayOrEmpty(match?.away?.players)) apply(player?.playerId, 'away', match?.away?.legs, player?.tons, player?.closes);
    }
    for (const player of this.beerPlayers('home')) apply(player?.playerId, 'home', this.sheet?.beer?.winner === 'home' ? 1 : 0, player?.tons, player?.closes);
    for (const player of this.beerPlayers('away')) apply(player?.playerId, 'away', this.sheet?.beer?.winner === 'away' ? 1 : 0, player?.tons, player?.closes);

    return [...rows.values()]
      .map((row) => ({
        ...row,
        avgTons: row.entries ? (row.tons / row.entries) : 0,
        avgCheckouts: row.entries ? (row.closes / row.entries) : 0,
      }))
      .sort((a, b) =>
        (b.legsWon - a.legsWon) ||
        (b.tons - a.tons) ||
        (b.highestCheckout - a.highestCheckout) ||
        a.name.localeCompare(b.name)
      );
  },

  openLightbox() {
    if (!this.sheet?.meta?.uploadUrl || this.sheet?.forfeit) return;
    this.lightboxOpen = true;
    document.body.classList.add('overflow-hidden');
  },

  closeLightbox() {
    this.lightboxOpen = false;
    if (!this.shareOpen) document.body.classList.remove('overflow-hidden');
  },
}));

Alpine.data('scoresheetsList', () => ({
  items: [],
  loading: true,
  refreshSpinning: false,
  firstLoad: true,
  page: 1,
  totalPages: 1,
  total: 0,
  filters: {
    status: 'submitted',
    q: '',
    limit: 20,
  },

  async init() {
    await this.reload();
    this.firstLoad = false;
  },

  buildQuery() {
    const params = new URLSearchParams();
    if (this.filters.status) params.set('status', this.filters.status);
    if (this.filters.q) params.set('q', this.filters.q);
    params.set('page', String(this.page));
    params.set('limit', String(this.filters.limit));
    return params.toString();
  },

  async reload() {
    this.loading = true;
    try {
      const res = await apiFetch(`/scoresheets?${this.buildQuery()}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to load scoresheets');
      this.items = json.items || [];
      this.page = json.page || 1;
      this.totalPages = json.totalPages || 1;
      this.total = json.total || 0;
    } catch (err) {
      console.error(err);
      this.$store?.toastr?.error?.(err.message || 'Failed to load scoresheets');
      this.items = [];
    } finally {
      this.loading = false;
      this.refreshSpinning = false;
    }
  },

  async manualRefresh() {
    this.refreshSpinning = true;
    await this.reload();
  },

  async go(page) {
    const next = Math.min(Math.max(1, page), this.totalPages || 1);
    if (next === this.page) return;
    this.page = next;
    await this.reload();
  },

  statusLabel(status) {
    if (status === 'approved') return 'Approved';
    if (status === 'submitted') return 'Needs Review';
    if (status === 'void') return 'Voided';
    return 'Draft';
  },

  statusClass(status) {
    if (status === 'approved') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';
    if (status === 'submitted') return 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300';
    if (status === 'void') return 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300';
    return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
  },

  formatDate(dt) {
    if (!dt) return 'Date TBC';
    const d = new Date(dt);
    return d.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Bangkok',
    });
  },

  formatDateTime(dt) {
    if (!dt) return 'Not recorded';
    const d = new Date(dt);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Bangkok',
    });
  },

  resultText(item) {
    const home = Number(item?.totals?.match?.[0] ?? item?.fixture?.result?.[0] ?? 0);
    const away = Number(item?.totals?.match?.[1] ?? item?.fixture?.result?.[1] ?? 0);
    if (item?.forfeit) return `${home}–${away} (forfeit)`;
    return `${home} - ${away}`;
  },

  async approve(item) {
    if (!item?._id) return;
    if (!confirm('Approve this scoresheet? This will finalize stats and the fixture.')) return;
    try {
      const res = await apiFetch(`/scoresheets/${item._id}/approve`, { method: 'PATCH' });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Approval failed');
      this.$store?.toastr?.success?.('Scoresheet approved.');
      await this.reload();
    } catch (err) {
      this.$store?.toastr?.error?.(err.message || 'Approval failed');
    }
  },

  async voidSheet(item) {
    if (!item?._id) return;
    if (!confirm('Void this scoresheet? It will remain in the system but no longer count for review or fixture flow.')) return;
    try {
      const res = await apiFetch(`/scoresheets/${item._id}/void`, { method: 'PATCH' });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Void failed');
      this.$store?.toastr?.success?.('Scoresheet voided.');
      await this.reload();
    } catch (err) {
      this.$store?.toastr?.error?.(err.message || 'Void failed');
    }
  },
}));
